// TZFIX_ARG applied 20260727_175645
// UTC_FIX_HELPER: parsea fecha asumiendo UTC si no tiene sufijo de timezone
const _UTC = (v) => { if (!v) return new Date(NaN); const s = String(v); return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z'); };
import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import "./App.css";
import axios from "axios";
import OSINTScanner from './components/OSINTScanner';
import AdvancedWebScanner from './components/AdvancedWebScanner';
import NetworkEnumScanner from './components/NetworkEnumScanner';
import SecurityPostureScanner from './components/SecurityPostureScanner';
import AIAttackReadinessScanner from './components/AIAttackReadinessScanner';
import APISecurityScanner from './components/APISecurityScanner';
import WebHygieneScanner from './components/WebHygieneScanner';
import AIBotSecurityScanner from './components/AIBotSecurityScanner';
import DarkWebScanner from './components/DarkWebScanner';
import BrandImpersonationScanner from './components/BrandImpersonationScanner';
import AdvancedScannersGrid from './components/AdvancedScannersGrid';
import DemoChatbot from './components/DemoChatbot';
import MicrosegmentationScanner from './components/MicrosegmentationScanner';
import USBPolicyScanner from './components/USBPolicyScanner';
import InternalAgentScanner from './components/InternalAgentScanner';
import ExecutiveReport from './components/ExecutiveReport';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import CookieBanner from './components/CookieBanner';
import RiskManagement from './components/RiskManagement';
import ISO27001Management from './components/ISO27001Management';
import SIEMDashboard from "./components/SIEMDashboard";
import { QuoteRequestButton, QuoteRequestsAdmin } from "./components/QuoteRequest";
import KevThreatsPanel from "./components/KevThreatsPanel";
import SurfaceChangesPanel from "./components/SurfaceChangesPanel";
import { atlasTechniqueEs, atlasTacticEs, attackRefEs, categoryEs } from "./i18n/frameworksEs";
import UsersManagement from "./components/UsersManagement";
import { isAdmin, canOperate, roleLabel, ROLE_CATALOG } from "./roles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COSTILLATECH_LOGO = "/costillatech-logo.svg";
const COSTILLATECH_HEADER_LOGO = "/costillatech-header-logo.png";

// Axios interceptor: mantenimiento + sesion vencida
let __seguriaSessionExpiredFired = false;
window.__seguriaResetSessionExpired = () => { __seguriaSessionExpiredFired = false; };
axios.interceptors.response.use(
  response => response,
  error => {
    const __status = error.response?.status;
    if (__status === 503 && error.response?.data?.maintenance) {
      // Save maintenance message to localStorage
      const maintenanceMessage = error.response.data.message || 'Sistema en mantenimiento';
      localStorage.setItem('maintenance_message', maintenanceMessage);
      
      // Check if user is SuperAdmin
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.role !== 'superadmin') {
        // Redirect to maintenance page
        window.location.href = '/maintenance';
      }
    }
    if (__status === 401) {
      const __url = error.config?.url || '';
      const __isAuthCall = __url.includes('/auth/me') || __url.includes('/auth/login');
      const __hasToken = !!localStorage.getItem('token');
      if (!__isAuthCall && __hasToken && !__seguriaSessionExpiredFired) {
        __seguriaSessionExpiredFired = true;
        window.dispatchEvent(new Event('seguria-session-expired'));
        // Cortar la cadena para evitar alerts crudos de "Could not validate credentials"
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // ===== Manejo de sesion vencida =====
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const onExpired = () => { if (localStorage.getItem('token')) setSessionExpired(true); };
    window.addEventListener('seguria-session-expired', onExpired);
    return () => window.removeEventListener('seguria-session-expired', onExpired);
  }, []);

  useEffect(() => {
    if (!token || !user) return;
    const checkExp = () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && Date.now() >= payload.exp * 1000) {
          setSessionExpired(true);
        }
      } catch (e) { /* token no decodificable */ }
    };
    checkExp();
    const __id = setInterval(checkExp, 30000);
    return () => clearInterval(__id);
  }, [token, user]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          // Get dashboard to get company info
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const dashboardResponse = await axios.get(`${API}/dashboard`, { headers });
          setCompany({ 
            name: dashboardResponse.data.company_name,
            subscription_plan: dashboardResponse.data.subscription_plan,
            trial_expires_at: dashboardResponse.data.trial_expires_at
          });
        } catch (error) {
          // Token is invalid
          logout();
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      const { access_token, user: userData, company: companyData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      setCompany(companyData);
      localStorage.setItem('token', access_token);
      
      return { success: true };
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle Pydantic validation errors
          errorMessage = error.response.data.detail.map(err => 
            `${err.loc[err.loc.length-1]}: ${err.msg}`
          ).join(", ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = 'Invalid login credentials';
        }
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const register = async (email, password, fullName, companyName, termsAccepted, legalAcceptanceData = null) => {
    try {
      const requestBody = {
        email,
        password,
        full_name: fullName,
        company_name: companyName,
        terms_accepted: termsAccepted
      };

      // Add legal acceptance data if provided
      if (legalAcceptanceData) {
        requestBody.legal_acceptance_data = legalAcceptanceData;
      }

      const response = await axios.post(`${API}/auth/register`, requestBody);
      const { access_token, user: userData, company: companyData } = response.data;

      setToken(access_token);
      setUser(userData);
      setCompany(companyData);
      localStorage.setItem('token', access_token);

      return { success: true };
    } catch (error) {
      let errorMessage = 'Registration failed';

      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle Pydantic validation errors
          errorMessage = error.response.data.detail.map(err =>
            `${err.loc[err.loc.length-1]}: ${err.msg}`
          ).join(", ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = 'Registration validation error';
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setCompany(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
  };

  // MercadoPago Payment Functions
  const createPaymentPreference = async (planId, payerEmail) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/payments/create-preference`, {
        plan_id: planId,
        payer_email: payerEmail,
        success_url: `${window.location.origin}/payment/success`,
        failure_url: `${window.location.origin}/payment/failure`,
        pending_url: `${window.location.origin}/payment/pending`
      }, { headers });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error creating payment preference'
      };
    }
  };

  const handlePlanPurchase = async (plan) => {
    console.log('📧 handlePlanPurchase (email flow) called with:', plan);
    if (!user || !user.email) {
      alert('Debes estar logueado para solicitar una contratación');
      return;
    }
    if (!window.confirm(`¿Confirmás la solicitud del plan "${plan.name}"?\n\nEl equipo comercial de Costillatech.com se contactará con vos para coordinar la activación y facturación.`)) {
      return;
    }
    try {
      const res = await axios.post(`${API}/plans/request-contract`, {
        plan_id: plan.id,
        plan_code: plan.plan_code
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.success !== false) {
        alert("✅ " + (res.data.message || "Solicitud enviada correctamente. Te contactaremos en breve."));
      } else {
        alert("⚠️ " + (res.data.message || "La solicitud fue registrada pero el envío del correo falló."));
      }
    } catch (error) {
      console.error("request-contract error:", error);
      const msg = error?.response?.data?.detail || error?.message || "Error procesando la solicitud";
      alert(`Error: ${msg}`);
    }
  };

  const value = {
    user,
    company,
    token,
    loading,
    login,
    register,
    logout,
    createPaymentPreference,
    handlePlanPurchase,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionExpired && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" data-testid="session-expired-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 text-center">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" d="M12 7v5l3 2"/></svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Sesión expirada</h3>
            <p className="text-gray-600 mb-6">Por seguridad, tu sesión se cerró por inactividad. Volvé a iniciar sesión para continuar.</p>
            <button
              data-testid="session-expired-ok"
              onClick={() => { setSessionExpired(false); if (window.__seguriaResetSessionExpired) window.__seguriaResetSessionExpired(); logout(); }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              Volver a iniciar sesión
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

// Enhanced Legal Acceptance Modal with Clickwrap Agreement
const TermsModal = ({ isOpen, onClose, onAccept, onDecline }) => {
  const [activeTab, setActiveTab] = useState('terms');
  const [authorizedDomains, setAuthorizedDomains] = useState('');
  const [authorizationStatement, setAuthorizationStatement] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [acknowledgeRead, setAcknowledgeRead] = useState(false);
  const [startTime] = useState(Date.now());

  const tabs = [
    { key: 'terms', label: 'Términos y Condiciones', url: '/legal/terms-v1.0.html' },
    { key: 'privacy', label: 'Privacidad', url: '/legal/privacy-v1.0.html' },
    { key: 'msa', label: 'Contrato de Servicios', url: '/legal/msa-v1.0.html' },
    { key: 'acceptable_use', label: 'Uso Aceptable', url: '/legal/acceptable-use-v1.0.html' }
  ];

  const handleAcceptTerms = () => {
    if (!acknowledgeRead) {
      alert('Por favor, confirma que has leído los documentos legales.');
      return;
    }

    if (!authorizedDomains.trim()) {
      alert('Por favor, especifica al menos un dominio o IP que planeas escanear.');
      return;
    }

    if (!authorizationStatement.trim() || authorizationStatement.length < 50) {
      alert('Por favor, escribe una declaración de autorización de al menos 50 caracteres.');
      return;
    }

    if (!agreed) {
      alert('Debes marcar el checkbox de aceptación para continuar.');
      return;
    }

    // Calculate time spent
    const timeSpentSeconds = Math.floor((Date.now() - startTime) / 1000);

    // Parse authorized targets
    const targets = authorizedDomains
      .split(/[,\n]/)
      .map(t => t.trim())
      .filter(t => t.length > 0);

    // Prepare legal acceptance data
    const legalAcceptanceData = {
      documents_accepted: [
        { type: 'terms', version: '1.0', hash: 'a4b3c8d5e9f2a1b7c4d6e8f1a3b5c7d9e2f4a6b8c1d3e5f7a9b2c4d6e8f1a3b5' },
        { type: 'privacy', version: '1.0', hash: 'b5c7d9e2f4a6b8c1d3e5f7a9b2c4d6e8f1a3b5c7d9e2f4a6b8c1d3e5f7a9b2c4' },
        { type: 'msa', version: '1.0', hash: 'c8d5e9f2a1b7c4d6e8f1a3b5c7d9e2f4a6b8c1d3e5f7a9b2c4d6e8f1a3b5c7d9' },
        { type: 'acceptable_use', version: '1.0', hash: 'd9e2f4a6b8c1d3e5f7a9b2c4d6e8f1a3b5c7d9e2f4a6b8c1d3e5f7a9b2c4d6e8' }
      ],
      authorization_statement: authorizationStatement,
      authorized_targets: targets,
      time_spent_seconds: timeSpentSeconds,
      scroll_percentage: 100
    };

    onAccept(legalAcceptanceData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="h-10 w-auto mr-3" />
              Aceptación Legal - Clickwrap Agreement
            </h2>
          </div>
        </div>

        {/* Botones para abrir documentos en nuevas pestañas */}
        <div className="px-6 py-3 bg-blue-50 border-b">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm text-gray-600">📄 Abrir documentos en nueva pestaña:</span>
            <div className="flex flex-wrap gap-2">
              <a href="/legal/terms-v1.0.html" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors">Términos</a>
              <a href="/legal/privacy-v1.0.html" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors">Privacidad</a>
              <a href="/legal/msa-v1.0.html" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors">Contrato</a>
              <a href="/legal/acceptable-use-v1.0.html" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors">Uso Aceptable</a>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b overflow-x-auto flex-shrink-0">
          <div className="flex space-x-1 px-6">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Document Content - con scroll interno */}
        <div className="overflow-y-auto p-6 bg-gray-50" style={{ height: '60vh', maxHeight: '60vh' }}>
          {tabs.map(tab => (
            <div
              key={tab.key}
              className={activeTab === tab.key ? 'block' : 'hidden'}
            >
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <iframe
                  src={tab.url}
                  className="w-full border-0"
                  style={{ height: '1000px', minHeight: '1000px' }}
                  title={tab.label}
                />
              </div>
            </div>
          ))}
          </div>

        {/* Authorization Section - Fixed at bottom */}
        <div className="p-4 border-t bg-white flex-shrink-0 space-y-1">
         {/* Acknowledgment - Moved here to be always visible */}
         <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
           <div className="flex items-start">
             <input
               type="checkbox"
               id="acknowledgeRead"
               checked={acknowledgeRead}
               onChange={(e) => setAcknowledgeRead(e.target.checked)}
               className="mt-1 h-4 w-4 text-blue-600"
            />
            <label htmlFor="acknowledgeRead" className="ml-3 text-sm text-gray-700">
              ✓ Confirmo que he revisado los 4 documentos legales (Términos, Privacidad, MSA, Uso Aceptable)
            </label>
           </div>
          </div>   
         <div>
            <label className="block text-sm font-medium text-gray-700 mb-0.5">
              Dominios/IPs que planeas escanear (separados por comas o líneas)
            </label>
            <textarea
              value={authorizedDomains}
              onChange={(e) => setAuthorizedDomains(e.target.value)}
              placeholder="ejemplo.com, *.ejemplo.com, 203.0.113.0/24"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="1"
            />
         </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Declaración de Autorización (mínimo 50 caracteres)
            </label>
            <textarea
              value={authorizationStatement}
              onChange={(e) => setAuthorizationStatement(e.target.value)}
              placeholder="Yo, [Nombre], en mi calidad de [Cargo] de [Empresa], declaro que tengo autorización expresa para realizar escaneos de seguridad en los dominios e IPs especificados arriba..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="1"
            />
            <div className="text-xs text-gray-500 mt-1">
              {authorizationStatement.length} / 50 caracteres mínimos
            </div>
          </div>
        <div className="flex items-center justify-between gap-4">
            <div className="flex items-start flex-1">
              <input
                type="checkbox"
                id="agreeTerms"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 flex-shrink-0"
              />
              <label htmlFor="agreeTerms" className="ml-2 text-xs text-gray-700">
                Declaro bajo juramento que tengo autorización legal para escanear los dominios/IPs especificados
                y que utilizaré esta plataforma únicamente para pentesting ético y auditorías autorizadas.
              </label>
            </div>

            <div className="flex space-x-2 flex-shrink-0">
              <button
                onClick={onDecline}
                className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Rechazar
              </button>
              <button
                onClick={handleAcceptTerms}
                disabled={!acknowledgeRead || !agreed}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aceptar y Registrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Welcome/Landing Component
const WelcomePage = ({ onGetStarted }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
      {/* Header */}
      <div className="flex justify-center pt-8 pb-16">
        <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="h-16 w-auto" />
      </div>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 text-center text-white">
        <h1 className="text-5xl font-bold mb-6">
          Bienvenido a Costillatech.com
        </h1>
        <p className="text-xl mb-8 text-blue-100">
          Plataforma de Auditoría de Seguridad Ética y Pentesting Profesional
        </p>
        
        {/* Security Standards */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-blue-200">
            Certificados y Estándares de Seguridad
          </h2>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            <div className="flex items-center bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                ISO
              </div>
              <div className="text-left">
                <p className="font-semibold">ISO 27001</p>
                <p className="text-sm text-blue-200">Gestión de Seguridad</p>
              </div>
            </div>
            
            <div className="flex items-center bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                OW
              </div>
              <div className="text-left">
                <p className="font-semibold">OWASP Top 10</p>
                <p className="text-sm text-blue-200">Seguridad Web</p>
              </div>
            </div>
            
            <div className="flex items-center bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                NI
              </div>
              <div className="text-left">
                <p className="font-semibold">NIST Framework</p>
                <p className="text-sm text-blue-200">Ciberseguridad</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white/10 backdrop-blur rounded-lg p-6">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-xl font-semibold mb-2">Pentesting Ético</h3>
            <p className="text-blue-200">Auditorías de seguridad profesionales con metodologías certificadas</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur rounded-lg p-6">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">Reportes Detallados</h3>
            <p className="text-blue-200">Análisis completos con recomendaciones de remediación</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur rounded-lg p-6">
            <div className="text-4xl mb-4">🔒</div>
            <h3 className="text-xl font-semibold mb-2">Cumplimiento</h3>
            <p className="text-blue-200">Mapeo automático a estándares ISO 27001, OWASP y NIST</p>
          </div>
        </div>
        
        {/* Transparency Section */}
        <div className="mb-12 p-6 bg-white/10 backdrop-blur rounded-lg">
          <h3 className="text-xl font-semibold mb-4">🔍 Transparencia del Código</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <ul className="text-sm space-y-2">
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Algoritmos de escaneo transparentes
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Sin backdoors ocultos
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Detección ética de vulnerabilidades
              </li>
            </ul>
            <ul className="text-sm space-y-2">
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Sin malware o troyanos
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Privacidad y aislamiento de datos
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Solo hacking ético autorizado
              </li>
            </ul>
          </div>
        </div>
        
        {/* CTA Button */}
        <button
          onClick={onGetStarted}
          className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors"
        >
          Comenzar Prueba Gratuita (1 Día)
        </button>
        
        <p className="text-sm text-blue-200 mt-4">
          Prueba gratuita por 1 día • Sin tarjeta de crédito requerida
        </p>
      </div>
    </div>
  );
};

// Eye icon for password toggle (login/register)
const EyeIcon = ({ open }) => (
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  )
);

// Login Component
// Badge de version: permite saber que producto/version/entorno se esta usando
const VersionBadge = () => {
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => {
    axios.get(`${API}/version`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  if (!info) return null;
  return (
    <p className="mt-2 text-xs text-gray-400 tracking-wide" data-testid="version-badge">
      {info.product} v{info.version} · {info.environment}
    </p>
  );
};

const LoginForm = ({ onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="mx-auto h-20 w-auto mb-4" />
          <h2 className="text-3xl font-extrabold text-gray-900">
            Iniciar Sesión en Costillatech.com
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Plataforma de Auditoría de Seguridad Ética
          </p>
          <VersionBadge />
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="tu@empresa.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Contraseña"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  data-testid="login-password-toggle"
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
              <div className="mt-2 text-right">
                <a href="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-500" data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              ¿No tienes cuenta? Regístrate aquí
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Register Component
const RegisterForm = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalAcceptanceData, setLegalAcceptanceData] = useState(null);
  const { register } = useAuth();

  const handleContinueToLegal = (e) => {
    e.preventDefault();
    
    if (!fullName || !email || !companyName || !password) {
      setError('Por favor completa todos los campos');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingresa un email válido');
      return;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setError('');
    setShowTerms(true);
  };


    const handleTermsAccept = async (legalData) => {
    setLegalAcceptanceData(legalData);
    setTermsAccepted(true);
    setShowTerms(false);
    
    // Registrar automáticamente después de aceptar términos
    setLoading(true);
    setError('');

    const result = await register(email, password, fullName, companyName, true, legalData);

    if (!result.success) {
      setError(result.error);
      setLoading(false);
    }
    // Si es exitoso, el register() manejará la redirección
  };

  const handleTermsDecline = () => {
    setLegalAcceptanceData(null);
    setTermsAccepted(false);
    setShowTerms(false);
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="mx-auto h-20 w-auto mb-4" />
            <h2 className="text-3xl font-extrabold text-gray-900">
              Registro en Costillatech.com
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Prueba gratuita por 1 día - Sin tarjeta requerida
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={(e) => e.preventDefault()}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {error}
                    </h3>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                  Nombre Completo
                </label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Juan Pérez"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Empresarial
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="juan@empresa.com"
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900"
                   data-testid="register-role-notice">
                Este registro crea una <strong>empresa nueva</strong> y vos quedás como su{' '}
                <strong>Administrador</strong>: vas a poder dar de alta a tu equipo, asignarles roles,
                autorizar los objetivos a escanear y gestionar el plan.
                <br />
                Si tu empresa <strong>ya usa Costillatech.com</strong>, no te registres acá: pedile a un administrador
                de tu empresa que te cree el usuario desde <em>Usuarios y Roles</em>.
              </div>

              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                  Nombre de la Empresa
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Empresa de Seguridad SA"
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Contraseña
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contraseña segura"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="register-password-toggle"
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </div>
            </div>

            {/* Botón para continuar a aceptación legal */}
            <div>
              <button
                type="button"
                onClick={handleContinueToLegal}
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Procesando...' : 'Continuar con Aceptación Legal →'}
              </button>
              <p className="mt-2 text-xs text-center text-gray-500">
                En el siguiente paso deberás aceptar los términos legales y declarar los dominios autorizados
              </p>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                ¿Ya tienes cuenta? Inicia sesión aquí
              </button>
            </div>
          </form>
        </div>
      </div>

      <TermsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </>
  );
};

// Enhanced Progress Component for Scans
const ScanProgress = ({ scan }) => {
  const getStepColor = (status) => {
    switch (status) {
      case 'initializing': return 'bg-blue-500';
      case 'port_scanning': return 'bg-yellow-500';
      case 'database_discovery': return 'bg-indigo-500';
      case 'database_testing': return 'bg-purple-500';
      case 'web_application_testing': return 'bg-cyan-500';
      case 'infrastructure_testing': return 'bg-teal-500';
      case 'advanced_analysis': return 'bg-red-500';
      case 'security_configuration': return 'bg-gray-500';
      case 'vulnerability_analysis': return 'bg-orange-500';
      case 'generating_report': return 'bg-pink-500';
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const steps = [
    { key: 'initializing', label: 'Iniciar', icon: '⚡' },
    { key: 'port_scanning', label: 'Puertos', icon: '🔍' },
    { key: 'database_discovery', label: 'DB Discovery', icon: '🗄️' },
    { key: 'database_testing', label: 'DB Testing', icon: '🔬' },
    { key: 'web_application_testing', label: 'Web Tests', icon: '🌐' },
    { key: 'infrastructure_testing', label: 'Infrastructure', icon: '🏗️' },
    { key: 'advanced_analysis', label: 'CVE/Risk', icon: '🎯' },
    { key: 'security_configuration', label: 'Config', icon: '⚙️' },
    { key: 'vulnerability_analysis', label: 'Analizar', icon: '⚠️' },
    { key: 'generating_report', label: 'Reporte', icon: '📊' },
    { key: 'completed', label: 'Completado', icon: '✅' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === scan.status);

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div 
          className={`h-3 rounded-full transition-all duration-300 ${getStepColor(scan.status)}`}
          style={{width: `${scan.progress}%`}}
        ></div>
      </div>
      
      {/* Current Step */}
      <div className="text-sm text-gray-700">
        <span className="font-medium">Estado:</span> {scan.current_step}
      </div>
      
      {/* Step Indicators */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.key} className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm
              ${index <= currentStepIndex ? getStepColor(scan.status) : 'bg-gray-300'}`}>
              {step.icon}
            </div>
            <span className="text-xs mt-1 text-gray-600">{step.label}</span>
          </div>
        ))}
      </div>
      
      <div className="text-xs text-gray-500">
        Progreso: {scan.progress}% | {scan.status === 'completed' ? 'Finalizado' : 'Ejecutando...'}
      </div>
    </div>
  );
};

// Plan Limits Warning Component
const PlanLimitsWarning = ({ usage, limits, planName }) => {
  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.round((used / limit) * 100);
  };

  const isNearLimit = (used, limit, threshold = 80) => {
    if (limit === -1) return false;
    return (used / limit) * 100 >= threshold;
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <h3 className="text-yellow-800 font-medium mb-3">📊 Plan {planName} - Uso Actual</h3>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Objetivos: {usage.targets}/{limits.targets === -1 ? '∞' : limits.targets}</span>
            <span>{limits.targets !== -1 ? getUsagePercentage(usage.targets, limits.targets) + '%' : ''}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${isNearLimit(usage.targets, limits.targets) ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{width: `${limits.targets === -1 ? 0 : Math.min(100, getUsagePercentage(usage.targets, limits.targets))}%`}}
            ></div>
          </div>
        </div>
        
        <div>
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Escaneos: {usage.monthly_scans}/{limits.scans_per_month === -1 ? '∞' : limits.scans_per_month}</span>
            <span>{limits.scans_per_month !== -1 ? getUsagePercentage(usage.monthly_scans, limits.scans_per_month) + '%' : ''}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${isNearLimit(usage.monthly_scans, limits.scans_per_month) ? 'bg-red-500' : 'bg-green-500'}`}
              style={{width: `${limits.scans_per_month === -1 ? 0 : Math.min(100, getUsagePercentage(usage.monthly_scans, limits.scans_per_month))}%`}}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component (Updated with SaaS features)
const Dashboard = ({ refreshTrigger = 0, showPlans = false }) => {
  const [stats, setStats] = useState({
    total_targets: 0,
    active_scans: 0,
    total_vulnerabilities: 0,
    critical_vulnerabilities: 0,
    high_vulnerabilities: 0,
    recent_scans: [],
    company_name: '',
    subscription_plan: 'trial',
    trial_expires_at: null,
    plan_limits: {},
    current_usage: {}
  });
  const [loading, setLoading] = useState(true);
  const { company, user, token, handlePlanPurchase } = useAuth();
  
  // Using handlePlanPurchase from AuthContext instead of local implementation

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/dashboard`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 15000);
    return () => clearInterval(interval);
  }, [refreshTrigger]); // Re-fetch when refreshTrigger changes

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Helper functions moved to MainApp component

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining(stats.trial_expires_at);

  return (
    <div>
      {/* Trial Warning */}
      {stats.subscription_plan === 'trial' && daysRemaining !== null && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-orange-600">⏰</div>
            <div className="ml-3">
              <h3 className="text-orange-800 font-medium">
                Prueba Gratuita - {daysRemaining > 0 ? `${daysRemaining} días restantes` : 'Expirada'}
              </h3>
              <p className="text-orange-700 text-sm">
                {daysRemaining > 0 
                  ? 'Actualiza tu plan para continuar usando todas las funcionalidades después del período de prueba.'
                  : 'Tu prueba gratuita ha expirado. Actualiza tu plan para continuar.'
                }
              </p>
            </div>
            {showPlans ? (
            <button
              data-testid="trial-view-plans-btn"
              onClick={() => {
                const element = document.evaluate("//h2[contains(text(),'Planes de Suscripción')]", document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }
              }}
              className="ml-auto bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700"
            >
              Ver Planes
            </button>
            ) : (
            <QuoteRequestButton />
            )}
          </div>
        </div>
      )}

      {/* Plan Usage */}
      {stats.plan_limits && stats.current_usage && (
        <PlanLimitsWarning
          usage={stats.current_usage}
          limits={stats.plan_limits}
          planName={stats.subscription_plan.charAt(0).toUpperCase() + stats.subscription_plan.slice(1)}
        />
      )}

      {/* Company Welcome */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="h-8 w-auto mr-3" />
              ¡Bienvenido de nuevo!
            </h2>
            <p className="text-gray-600">{stats.company_name} - Dashboard de Seguridad</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-700">Plan: {stats.subscription_plan.toUpperCase()}</p>
            {stats.trial_expires_at && (
              <p className="text-xs text-gray-500">
                Expira: {_UTC(stats.trial_expires_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {/* Stats Cards */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Objetivos Totales</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_targets}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Escaneos Activos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active_scans}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Vulnerabilidades</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_vulnerabilities}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Críticas/Altas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.critical_vulnerabilities + stats.high_vulnerabilities}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Scans with Enhanced Progress */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Escaneos Recientes</h3>
        {stats.recent_scans.length > 0 ? (
          <div className="space-y-4">
            {stats.recent_scans.slice(0, 3).map((scan) => (
              <div key={scan.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{scan.scan_type} Scan</h4>
                    <p className="text-sm text-gray-600">
                      Iniciado: {_UTC(scan.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(scan.status)}`}>
                    {scan.status}
                  </span>
                </div>
                
                {(scan.status === 'initializing' || scan.status === 'port_scanning' || 
                  scan.status === 'database_discovery' || scan.status === 'database_testing' ||
                  scan.status === 'web_application_testing' || scan.status === 'infrastructure_testing' ||
                  scan.status === 'advanced_analysis' || scan.status === 'security_configuration' ||
                  scan.status === 'vulnerability_analysis' || scan.status === 'generating_report') && (
                  <ScanProgress scan={scan} />
                )}
                
                {scan.status === 'completed' && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-green-600">✅ Completado - </span>
                    <span className="font-medium">Vulnerabilidades encontradas: </span>
                    <span className="text-red-600 font-semibold">{scan.vulnerabilities_found}</span>
                  </div>
                )}
                
                {scan.status === 'failed' && scan.error_message && (
                  <div className="text-sm text-red-600">
                    <span className="font-medium">❌ Error: </span>
                    {scan.error_message}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No hay escaneos recientes. Inicia tu primer escaneo para ver el progreso aquí.</p>
        )}
      </div>
    </div>
  );
};

// Target Management Component (Complete implementation)
const TargetManager = ({ onTargetAdded }) => {
  const [targets, setTargets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port_range: '',
    port_profile: 'aplicaciones',
    target_type: 'server',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/targets`, { headers });
      setTargets(response.data);
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  useEffect(() => {
    fetchTargets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/targets`, formData);
      setShowForm(false);
      setFormData({
        name: '',
        host: '',
        port_range: '',
        port_profile: 'aplicaciones',
        target_type: 'server',
        description: ''
      });
      fetchTargets();
      if (onTargetAdded) onTargetAdded();
    } catch (error) {
      alert("Error creando target: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (targetId) => {
    if (window.confirm("¿Estás seguro de eliminar este target y todos los datos asociados?")) {
      try {
        await axios.delete(`${API}/targets/${targetId}`);
        fetchTargets();
        if (onTargetAdded) onTargetAdded();
      } catch (error) {
        alert("Error eliminando target: " + (error.response?.data?.detail || error.message));
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
          Gestión de Objetivos
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Agregar Objetivo
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Objetivo</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="Mi Servidor Web"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Host (IP o Dominio)</label>
              <input
                type="text"
                required
                value={formData.host}
                onChange={(e) => setFormData({...formData, host: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="192.168.1.1 o ejemplo.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Puertos a escanear</label>
              <select
                value={formData.port_profile}
                onChange={(e) => setFormData({...formData, port_profile: e.target.value, port_range: ''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                data-testid="target-port-profile"
              >
                <option value="aplicaciones">Aplicaciones y paneles internos (recomendado)</option>
                <option value="comunes">Solo puertos comunes (1-1024)</option>
                <option value="intensivo">Intensivo (1-10000, más lento)</option>
                <option value="personalizado">Personalizado...</option>
              </select>
              {formData.port_profile === 'personalizado' && (
                <input
                  type="text"
                  value={formData.port_range}
                  onChange={(e) => setFormData({...formData, port_range: e.target.value})}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="1-1024,8000-8100,58021"
                  data-testid="target-port-range"
                />
              )}
              <p className="mt-1 text-xs text-gray-500">
                El perfil recomendado incluye los puertos altos donde suelen vivir turneros, tablets y paneles internos.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Objetivo</label>
              <select
                value={formData.target_type}
                onChange={(e) => setFormData({...formData, target_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                <option value="server">Servidor</option>
                <option value="web_app">Aplicación Web</option>
                <option value="network">Red</option>
                <option value="cloud">Servicio en la Nube</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              rows="2"
              placeholder="Descripción opcional..."
            />
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? "Creando..." : "Crear Objetivo"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {targets.map((target) => (
          <div key={target.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-gray-900">{target.name}</h3>
                <p className="text-gray-600">{target.host} ({target.port_range})</p>
                <p className="text-sm text-gray-500">Tipo: {target.target_type}</p>
                {target.description && <p className="text-sm text-gray-600 mt-1">{target.description}</p>}
                <p className="text-xs text-gray-400">Creado: {_UTC(target.created_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDelete(target.id)}
                  className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                  title="Eliminar Objetivo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
        {targets.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay targets configurados</h3>
            <p className="text-gray-500 mb-4">Agrega tu primer target para comenzar con los escaneos de seguridad</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Crear Primer Objetivo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Scan Management Component (Complete implementation)
const ScanManager = ({ refreshDashboard }) => {
  const [targets, setTargets] = useState([]);
  const [scans, setScans] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [scanType, setScanType] = useState('network');
  const [scanPortRange, setScanPortRange] = useState('');
  const [loading, setLoading] = useState(false);
  
  // SuperAdmin: selector de empresa
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          if (response.data.role === 'superadmin') {
            fetchCompanies();
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clientCompanies = response.data.filter(c => c.name !== 'Costillatech.com Platform' && c.name !== 'Costillatech.com');
      setCompanies(clientCompanies);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/targets`;
      if (user?.role === 'superadmin' && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, { headers });
      setTargets(response.data);
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/scans`;
      if (user?.role === 'superadmin' && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, { headers });
      setScans(response.data);
    } catch (error) {
      console.error("Error fetching scans:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchTargets();
      fetchScans();
      const interval = setInterval(fetchScans, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedCompanyId, user]);

  const startScan = async (e) => {
    e.preventDefault();
    if (!selectedTarget) {
      alert("Por favor selecciona un target");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/scans`, {
        target_id: selectedTarget,
        scan_type: scanType,
        port_range: scanPortRange.trim()
      });
      setSelectedTarget('');
      fetchScans();
      if (refreshDashboard) refreshDashboard();
    } catch (error) {
      alert("Error iniciando escaneo: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getScanTypeDisplay = (type) => {
    const types = {
      'network': 'Red',
      'database': 'Base de Datos',
      'web_app': 'Aplicación Web',
      'infrastructure': 'Infraestructura',
      'full': 'Completo',
      'api_security': 'API Security',
      'microsegmentation': 'Microsegmentación',
      'ai_attack_readiness': 'AI Attack Readiness',
      'ai_bot_security': 'AI Bot Security',
      'dark_web': 'Dark Web',
      'brand_impersonation': 'Brand Impersonation',
      'advanced_web': 'Web Avanzado',
      'osint': 'OSINT',
      'security_posture': 'Postura de Seguridad'
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Selector de empresa para SuperAdmin */}
      {user?.role === 'superadmin' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
            🔐 Modo SuperAdmin - Filtrar por Empresa
          </h3>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white"
          >
            <option value="">📊 Todas las empresas (consolidado)</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.total_scans} escaneos)
              </option>
            ))}
          </select>
          {selectedCompanyId ? (
            <p className="mt-2 text-sm text-red-600">
              ✓ Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>
            </p>
          ) : (
            <p className="mt-2 text-sm text-red-600">
              📊 Mostrando escaneos de todas las empresas
            </p>
          )}
        </div>
      )}
      
      {/* Start New Scan */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
          Iniciar Nuevo Escaneo
          {user?.role === 'superadmin' && !selectedCompanyId && (
            <span className="ml-2 text-sm font-normal text-gray-500">(Selecciona empresa para escanear)</span>
          )}
        </h2>
        
        <form onSubmit={startScan} className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Objetivo</label>
              <select
                value={selectedTarget}
                onChange={(e) => setSelectedTarget(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                required
              >
                <option value="">Elige un target...</option>
                {targets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.name} ({target.host})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Escaneo</label>
              <select
                value={scanType}
                onChange={(e) => setScanType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                <option value="network">Escaneo de Red</option>
                <option value="database">Escaneo de Bases de Datos</option>
                <option value="web_app">Aplicación Web</option>
                <option value="infrastructure">Infraestructura</option>
                <option value="full">Escaneo Completo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Puertos a escanear
              </label>
              <input
                type="text"
                value={scanPortRange}
                onChange={(e) => setScanPortRange(e.target.value)}
                placeholder="Vacío = TODOS (1-65535)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                data-testid="scan-port-range-input"
              />
              <div className="flex flex-wrap gap-1 mt-1">
                {[['', 'Todos'], ['1-1024', 'Comunes'], ['1-1024,8000-8100,8443,9000-9100,50000-50010,58000-58100', 'Aplicaciones'], ['1-10000', '1-10000']].map(([valor, etiqueta]) => (
                  <button
                    key={etiqueta}
                    type="button"
                    onClick={() => setScanPortRange(valor)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${scanPortRange === valor ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
                    data-testid={`scan-port-preset-${etiqueta.toLowerCase()}`}
                  >
                    {etiqueta}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={loading || !selectedTarget}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Iniciando..." : "Iniciar Escaneo"}
              </button>
            </div>
          </div>

          {scanPortRange.trim() === '' && (
            <div
              className="mt-4 flex items-start gap-3 p-4 rounded-lg border-2 border-amber-400 bg-amber-50 animate-pulse"
              data-testid="scan-all-ports-warning"
            >
              <span className="text-xl leading-none">⚠️</span>
              <div>
                <p className="text-sm font-bold text-amber-900">
                  Vas a escanear los 65.535 puertos del objetivo
                </p>
                <p className="text-sm text-amber-800">
                  Es el escaneo más completo, pero puede tardar varios minutos (aprox. 5 a 7).
                  Vas a ver el avance en vivo en el historial de abajo. Si querés algo más rápido,
                  elegí un rango con los atajos o escribilo a mano (ej. <span className="font-mono">1-1024,8000-8100</span>).
                </p>
              </div>
            </div>
          )}

          {scanPortRange.trim() !== '' && (
            <p className="mt-3 text-xs text-gray-600" data-testid="scan-port-range-hint">
              Se van a escanear solo los puertos <span className="font-mono font-semibold">{scanPortRange.trim()}</span>.
              Dejá el campo vacío para escanear todos.
            </p>
          )}
          
          {targets.length === 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                ⚠️ No hay targets disponibles. <a href="#" className="underline">Crea un target primero</a> para poder iniciar escaneos.
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Scan History */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial de Escaneos</h3>
        
        {scans.length > 0 ? (
          <div className="space-y-4">
            {scans.map((scan) => {
              const target = targets.find(t => t.id === scan.target_id);
              return (
                <div key={scan.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {target ? target.name : 'Objetivo Desconocido'} - {getScanTypeDisplay(scan.scan_type)}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Iniciado: {_UTC(scan.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}
                      </p>
                      {target && (
                        <p className="text-sm text-gray-500">
                          Host: {target.host} | Puertos: {scan.port_range || target.port_range}
                        </p>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      scan.status === 'completed' ? 'bg-green-100 text-green-600' :
                      scan.status === 'failed' ? 'bg-red-100 text-red-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {scan.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  
                  {['initializing', 'port_scanning', 'database_discovery', 'database_testing', 'web_application_testing', 'infrastructure_testing', 'advanced_analysis', 'security_configuration', 'vulnerability_analysis', 'generating_report'].includes(scan.status) && (
                    <ScanProgress scan={scan} />
                  )}
                  
                  {scan.status === 'completed' && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-green-800">✅ Escaneo Completado</span>
                          <div className="text-sm text-green-700 mt-1">
                            <span className="font-medium">Vulnerabilidades encontradas: </span>
                            <span className="text-red-600 font-semibold">{scan.vulnerabilities_found}</span>
                          </div>
                        </div>
                        <div className="text-right text-sm text-green-600">
                          {scan.completed_at && (
                            <div>Finalizado: {_UTC(scan.completed_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {scan.status === 'failed' && scan.error_message && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg">
                      <span className="font-medium text-red-800">❌ Error en Escaneo:</span>
                      <p className="text-sm text-red-700 mt-1">{scan.error_message}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay escaneos realizados</h3>
            <p className="text-gray-500 mb-4">Inicia tu primer escaneo de seguridad para comenzar</p>
            {targets.length > 0 && (
              <p className="text-sm text-blue-600">⬆️ Usa el formulario de arriba para iniciar un escaneo</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Vulnerability View Component (Complete implementation)
const VulnerabilityView = () => {
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [targets, setTargets] = useState([]);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterTarget, setFilterTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichStats, setEnrichStats] = useState(null);
  
  // SuperAdmin: selector de empresa
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          if (response.data.role === 'superadmin') {
            fetchCompanies();
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clientCompanies = response.data.filter(c => c.name !== 'Costillatech.com Platform' && c.name !== 'Costillatech.com');
      setCompanies(clientCompanies);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchVulnerabilities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterTarget) params.append('target_id', filterTarget);
      if (user?.role === 'superadmin' && selectedCompanyId) {
        params.append('company_id', selectedCompanyId);
      }
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/vulnerabilities?${params}`, { headers });
      setVulnerabilities(response.data);
    } catch (error) {
      console.error("Error fetching vulnerabilities:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/targets`;
      if (user?.role === 'superadmin' && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, { headers });
      setTargets(response.data);
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchVulnerabilities();
      fetchTargets();
    }
  }, [filterSeverity, filterTarget, selectedCompanyId, user]);

  const enrichCVEs = async () => {
    setEnriching(true);
    setEnrichStats(null);
    try {
      const token = localStorage.getItem('token');
      const params = selectedCompanyId ? `?company_id=${selectedCompanyId}` : '';
      const response = await axios.post(`${API}/cve/enrich${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrichStats(response.data.stats);
      // Refresh vulnerabilities after enrichment
      fetchVulnerabilities();
    } catch (error) {
      console.error('Error enriching CVEs:', error);
      alert('Error al enriquecer con CVEs: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEnriching(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-100 border-blue-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🔵';
      default: return '⚪';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Selector de empresa para SuperAdmin */}
      {user?.role === 'superadmin' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
            🔐 Modo SuperAdmin - Filtrar por Empresa
          </h3>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white"
          >
            <option value="">📊 Todas las empresas (consolidado)</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.total_vulnerabilities} vulns)
              </option>
            ))}
          </select>
          {selectedCompanyId && (
            <p className="mt-2 text-sm text-red-600">
              ✓ Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>
            </p>
          )}
          {!selectedCompanyId && (
            <p className="mt-2 text-sm text-red-600">
              📊 Mostrando datos consolidados de todas las empresas
            </p>
          )}
        </div>
      )}
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
          Análisis de Vulnerabilidades
          {user?.role === 'superadmin' && !selectedCompanyId && (
            <span className="ml-2 text-sm font-normal text-gray-500">(Consolidado)</span>
          )}
        </h2>
        <div className="flex space-x-4">
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="">Todas las Severidades</option>
            <option value="critical">🔴 Crítica</option>
            <option value="high">🟠 Alta</option>
            <option value="medium">🟡 Media</option>
            <option value="low">🔵 Baja</option>
            <option value="info">⚪ Informativa</option>
          </select>
          <select
            value={filterTarget}
            onChange={(e) => setFilterTarget(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="">Todos los Objetivos</option>
            {targets.map((target) => (
              <option key={target.id} value={target.id}>
                {target.name}
              </option>
            ))}
          </select>
          <button
            onClick={enrichCVEs}
            disabled={enriching}
            data-testid="cve-enrich-btn"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              enriching 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {enriching ? 'Consultando NVD...' : 'Enriquecer CVEs'}
          </button>
        </div>
      </div>

      {enrichStats && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between" data-testid="cve-enrich-stats">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-indigo-800">NVD Enrichment:</span>
            <span className="text-indigo-700">{enrichStats.enriched} enriquecidas</span>
            <span className="text-indigo-600">{enrichStats.cves_found} CVEs encontrados</span>
            <span className="text-gray-500">{enrichStats.skipped} sin version detectable</span>
          </div>
          <button onClick={() => setEnrichStats(null)} className="text-gray-400 hover:text-gray-600 text-xs">Cerrar</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {vulnerabilities.map((vuln) => {
            const target = targets.find(t => t.id === vuln.target_id);
            return (
              <div key={vuln.id} className={`border rounded-lg p-4 hover:bg-gray-50 ${getSeverityColor(vuln.severity)}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">{getSeverityIcon(vuln.severity)}</span>
                      {vuln.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Objetivo:</span> {target ? target.name : 'Desconocido'} | 
                      <span className="font-medium"> Puerto:</span> {vuln.port || 'N/A'} | 
                      <span className="font-medium"> Servicio:</span> {vuln.service || 'Desconocido'}
                      {vuln.database_type && (
                        <>
                          <span className="font-medium"> | DB:</span> {vuln.database_type.toUpperCase()}
                          {vuln.database_version && <span> v{vuln.database_version}</span>}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(vuln.severity)}`}>
                      {vuln.severity.toUpperCase()}
                    </span>
                    {vuln.cvss_score && (
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded border">
                        CVSS: {vuln.cvss_score}
                      </span>
                    )}
                    {vuln.days_exposed != null && (
                      <span data-testid={`exposure-badge-${vuln.id}`} className={`px-2 py-1 rounded text-xs font-semibold border ${
                        vuln.days_exposed > 90 ? 'bg-red-100 text-red-800 border-red-300' :
                        vuln.days_exposed > 30 ? 'bg-orange-100 text-orange-800 border-orange-300' :
                        vuln.days_exposed > 7 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                        'bg-green-100 text-green-800 border-green-300'
                      }`}>
                        {vuln.days_exposed}d expuesto
                      </span>
                    )}
                    {vuln.cve_id && (
                      <a 
                        href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`cve-badge-${vuln.id}`}
                        className="px-2 py-1 rounded text-xs font-mono font-medium bg-indigo-100 text-indigo-800 border border-indigo-300 hover:bg-indigo-200 transition-colors"
                      >
                        {vuln.cve_id}
                      </a>
                    )}
                  </div>
                </div>
                
                <p className="text-gray-700 mb-3">{vuln.description}</p>
                
                {vuln.exploit_suggestion && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 flex items-center">
                      ⚠️ Posible Explotación:
                    </p>
                    <p className="text-sm text-red-700 mt-1">{vuln.exploit_suggestion}</p>
                  </div>
                )}
                
                {vuln.remediation && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800 flex items-center">
                      🛡️ Recomendación de Seguridad:
                    </p>
                    <p className="text-sm text-green-700 mt-1">{vuln.remediation}</p>
                  </div>
                )}
                
                <div className="border-t pt-3 mt-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">📋 Mapeo a Estándares de Compliance:</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    {vuln.iso27001_risk_category && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          ISO 27001
                        </span>
                        {vuln.iso27001_risk_category}
                      </p>
                    )}
                    {vuln.owasp_category && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          OWASP
                        </span>
                        {vuln.owasp_category}
                      </p>
                    )}
                    {vuln.nist_category && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          NIST
                        </span>
                        {vuln.nist_category}
                      </p>
                    )}
                    {vuln.atlas_technique_id && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          MITRE ATLAS
                        </span>
                        {vuln.atlas_technique_id} - {atlasTechniqueEs(vuln.atlas_technique_id, vuln.atlas_technique)} ({atlasTacticEs(vuln.atlas_tactic)})
                      </p>
                    )}
                    {vuln.attack_technique && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-slate-200 text-slate-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          MITRE ATT&CK
                        </span>
                        {attackRefEs(vuln.attack_technique)}
                      </p>
                    )}
                    {vuln.pci_dss_requirement && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          PCI DSS
                        </span>
                        {vuln.pci_dss_requirement}
                      </p>
                    )}
                    {vuln.gdpr_article && (
                      <p className="flex items-center">
                        <span className="inline-flex items-center bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium mr-2">
                          GDPR
                        </span>
                        {vuln.gdpr_article}
                      </p>
                    )}
                  </div>
                  
                  {vuln.remediation_priority && (
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        /^(CRÍTICA|CRITICAL)/.test(vuln.remediation_priority) ? 'bg-red-100 text-red-800' :
                        /^(URGENTE|URGENT)/.test(vuln.remediation_priority) ? 'bg-orange-100 text-orange-800' :
                        /^(ALTA|HIGH)/.test(vuln.remediation_priority) ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        🕒 Prioridad: {vuln.remediation_priority}
                      </span>
                    </div>
                  )}
                </div>
                
                {vuln.related_cves && vuln.related_cves.length > 0 && (
                  <div className="border-t pt-3 mt-3" data-testid={`cve-section-${vuln.id}`}>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <span className="inline-flex items-center bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs font-medium mr-2">NVD</span>
                      CVEs Relacionados ({vuln.related_cves.length})
                    </h4>
                    <div className="space-y-1">
                      {vuln.related_cves.slice(0, 5).map((cve, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-1.5">
                          <a 
                            href={cve.nvd_url || `https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-mono font-medium"
                            data-testid={`cve-link-${cve.cve_id}`}
                          >
                            {cve.cve_id}
                          </a>
                          <div className="flex items-center gap-2">
                            {cve.cvss_score && (
                              <span className={`px-2 py-0.5 rounded font-semibold ${
                                cve.cvss_score >= 9.0 ? 'bg-red-100 text-red-800' :
                                cve.cvss_score >= 7.0 ? 'bg-orange-100 text-orange-800' :
                                cve.cvss_score >= 4.0 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                CVSS {cve.cvss_score}
                              </span>
                            )}
                            {cve.cvss_severity && (
                              <span className="text-gray-500">{cve.cvss_severity}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center text-xs text-gray-400 mt-3">
                  {vuln.days_exposed != null && (
                    <span data-testid={`exposure-detail-${vuln.id}`} className="flex items-center gap-1">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        vuln.days_exposed > 90 ? 'bg-red-500' :
                        vuln.days_exposed > 30 ? 'bg-orange-500' :
                        vuln.days_exposed > 7 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}></span>
                      Primera deteccion: {vuln.first_seen ? _UTC(vuln.first_seen).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'}) : 'N/A'} ({vuln.days_exposed} dias expuesto)
                    </span>
                  )}
                  <span>Detectado: {_UTC(vuln.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</span>
                </div>
              </div>
            );
          })}
          {vulnerabilities.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🛡️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filterSeverity || filterTarget ? 'No se encontraron vulnerabilidades con los filtros seleccionados' : 'No hay vulnerabilidades detectadas'}
              </h3>
              <p className="text-gray-500 mb-4">
                {filterSeverity || filterTarget 
                  ? 'Prueba cambiando los filtros de búsqueda'
                  : 'Las vulnerabilidades aparecerán aquí después de completar escaneos de seguridad'
                }
              </p>
              {!filterSeverity && !filterTarget && (
                <p className="text-sm text-blue-600">💡 Inicia un escaneo desde la pestaña "Escaneos" para comenzar</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Scan Details Component - Shows detailed scan steps and results
const ScanDetailsView = () => {
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // SuperAdmin: selector de empresa
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          if (response.data.role === 'superadmin') {
            fetchCompanies();
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clientCompanies = response.data.filter(c => c.name !== 'Costillatech.com Platform' && c.name !== 'Costillatech.com');
      setCompanies(clientCompanies);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchScans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/scans`;
      if (user?.role === 'superadmin' && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, { headers });
      setScans(response.data.filter(scan => scan.scan_details && scan.scan_details.length > 0));
    } catch (error) {
      console.error("Error fetching scans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchScans();
    }
  }, [selectedCompanyId, user]);

  const getScanDetailIcon = (status) => {
    switch (status) {
      case 'PASSED': return '✅';
      case 'VULNERABLE': return '🚨';
      case 'ISSUES_FOUND': return '⚠️';
      case 'ERROR': return '❌';
      case 'SKIPPED': return '⏭️';
      default: return '🔍';
    }
  };

  const getScanDetailColor = (status) => {
    switch (status) {
      case 'PASSED': return 'text-green-600 bg-green-50';
      case 'VULNERABLE': return 'text-red-600 bg-red-50';
      case 'ISSUES_FOUND': return 'text-yellow-600 bg-yellow-50';
      case 'ERROR': return 'text-red-600 bg-red-50';
      case 'SKIPPED': return 'text-gray-600 bg-gray-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  // Mapeo de tests a severidades
  const getTestSeverity = (testName, status) => {
    if (status === 'PASSED' || status === 'SKIPPED') return null;
    const severityMap = {
      'SQL Injection Test': 'CRITICAL',
      'Cross-Site Scripting (XSS) Test': 'HIGH',
      'CSRF Protection Test': 'MEDIUM',
      'Directory Traversal Test': 'HIGH',
      'Security Headers Test': 'MEDIUM',
      'Authentication Bypass Test': 'CRITICAL',
      'Sensitive Data Exposure Test': 'HIGH',
      'SSL/TLS Security Analysis': 'HIGH',
      'DNS Security Analysis': 'MEDIUM',
      'Mail Server Security Analysis': 'MEDIUM',
      'File Sharing Security Analysis': 'HIGH',
      'Cloud Security Analysis': 'MEDIUM',
      'Container Security Analysis': 'MEDIUM',
      'Password Policy Analysis': 'LOW',
      'Service Hardening Analysis': 'MEDIUM',
      'User Account Security Analysis': 'MEDIUM',
      'Patch Level Assessment': 'MEDIUM',
    };
    return severityMap[testName] || 'MEDIUM';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Selector de empresa para SuperAdmin */}
      {user?.role === 'superadmin' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
            🔐 Modo SuperAdmin - Filtrar por Empresa
          </h3>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white"
          >
            <option value="">📊 Todas las empresas (consolidado)</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.total_scans} escaneos)
              </option>
            ))}
          </select>
          {selectedCompanyId ? (
            <p className="mt-2 text-sm text-red-600">
              ✓ Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>
            </p>
          ) : (
            <p className="mt-2 text-sm text-red-600">
              📊 Mostrando detalles de todas las empresas
            </p>
          )}
        </div>
      )}
      
      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
        <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
        Detalles de Escaneos - Web Application Security Tests
        {user?.role === 'superadmin' && !selectedCompanyId && (
          <span className="ml-2 text-sm font-normal text-gray-500">(Consolidado)</span>
        )}
      </h2>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Scan Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecciona un escaneo para ver detalles:
            </label>
            <select
              value={selectedScan?.id || ''}
              onChange={(e) => {
                const scan = scans.find(s => s.id === e.target.value);
                setSelectedScan(scan);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <option value="">-- Selecciona un escaneo --</option>
              {scans.map((scan) => (
                <option key={scan.id} value={scan.id}>
                  {_UTC(scan.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})} - {scan.scan_type} ({scan.status})
                </option>
              ))}
            </select>
          </div>

          {/* Scan Details */}
          {selectedScan && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold text-gray-900 mb-4">
                Detalles del Escaneo - {selectedScan.scan_type.toUpperCase()}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Estado:</p>
                  <p className={`font-medium ${
                    selectedScan.status === 'completed' ? 'text-green-600' :
                    selectedScan.status === 'failed' ? 'text-red-600' :
                    'text-blue-600'
                  }`}>
                    {selectedScan.status.toUpperCase()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vulnerabilidades:</p>
                  <p className="font-medium text-orange-600">{selectedScan.vulnerabilities_found || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Iniciado:</p>
                  <p className="font-medium">{_UTC(selectedScan.started_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Completado:</p>
                  <p className="font-medium">
                    {selectedScan.completed_at ? _UTC(selectedScan.completed_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'}) : 'En proceso'}
                  </p>
                </div>
              </div>

              {/* Web Application Test Steps */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Tests Ejecutados (Aplicación Web):</h4>
                <div className="space-y-3">
                  {selectedScan.scan_details.map((step, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${getScanDetailColor(step.status)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <span className="text-lg mr-3">{getScanDetailIcon(step.status)}</span>
                          <div>
                            <h5 className="font-medium">{step.test}</h5>
                            <p className="text-sm mt-1">{step.message}</p>
                            {step.details && (
                              <p className="text-xs mt-1 opacity-75">{step.details}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                      {(() => {
                        const severity = getTestSeverity(step.test, step.status);
                        return severity && (
                          <span className={`px-2 py-1 text-xs font-bold rounded ${
                            severity === 'CRITICAL' ? 'bg-red-600 text-white' :
                            severity === 'HIGH' ? 'bg-orange-500 text-white' :
                            severity === 'MEDIUM' ? 'bg-yellow-500 text-white' :
                            'bg-blue-500 text-white'
                          }`}>
                            {severity}
                          </span>
                        );
                      })()}
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        step.status === 'PASSED' ? 'bg-green-100 text-green-800' :
                        step.status === 'VULNERABLE' ? 'bg-red-100 text-red-800' :
                        step.status === 'ISSUES_FOUND' ? 'bg-yellow-100 text-yellow-800' :
                        step.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {step.status}
                      </span>
                    </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {_UTC(step.timestamp).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <ScanVulnerabilitiesBlock scanId={selectedScan.id} />
            </div>
          )}

          {scans.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay detalles de escaneos disponibles</h3>
              <p className="text-gray-500 mb-4">Los detalles de escaneos web aparecerán aquí después de completar escaneos</p>
              <p className="text-sm text-blue-600">💡 Inicia un escaneo desde la pestaña "Escaneos" para ver detalles</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const ScanVulnerabilitiesBlock = ({ scanId }) => {
  const [vulns, setVulns] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (!scanId) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    axios.get(`${API}/vulnerabilities?scan_id=${scanId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setVulns(res.data || []))
      .catch(err => { console.error("fetch vulns:", err); setVulns([]); })
      .finally(() => setLoading(false));
  }, [scanId]);
  if (loading) return <div className="mt-6 text-sm text-gray-500">Cargando vulnerabilidades detectadas…</div>;
  if (!vulns || vulns.length === 0) return null;
  const sevColor = (s) => ({
    critical: "bg-red-100 text-red-800 border-red-300",
    high: "bg-orange-100 text-orange-800 border-orange-300",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-300",
    low: "bg-blue-100 text-blue-800 border-blue-300",
    info: "bg-gray-100 text-gray-700 border-gray-300",
  }[s] || "bg-gray-100 text-gray-700 border-gray-300");
  return (
    <div className="mt-6" data-testid="scan-vulns-block">
      <h4 className="font-medium text-gray-900 mb-3">
        Vulnerabilidades Detectadas ({vulns.length})
        <span className="ml-2 text-xs text-gray-500 font-normal">Incluye todos los módulos del escaneo</span>
      </h4>
      <div className="space-y-2">
        {vulns.map(v => (
          <div key={v.id} className={`p-3 rounded-lg border ${sevColor(v.severity)}`} data-testid={`scan-vuln-${v.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{v.title || v.name || "Vulnerabilidad"}</div>
                {v.description && <div className="text-xs mt-1 opacity-80 line-clamp-2">{v.description}</div>}
                {v.category && <div className="text-xs mt-1 opacity-70">Categoría: {categoryEs(v.category)}</div>}
              </div>
              <span className={`px-2 py-1 text-xs font-bold rounded uppercase whitespace-nowrap`}>{v.severity}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Risk Dashboard Component - Advanced risk analysis and metrics
const RiskDashboard = () => {
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // SuperAdmin: selector de empresa
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          if (response.data.role === 'superadmin') {
            fetchCompanies();
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clientCompanies = response.data.filter(c => c.name !== 'Costillatech.com Platform' && c.name !== 'Costillatech.com');
      setCompanies(clientCompanies);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchRiskData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/risk-dashboard`;
      if (user?.role === 'superadmin' && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, { headers });
      setRiskData(response.data);
    } catch (error) {
      console.error("Error fetching risk dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchRiskData();
    }
  }, [selectedCompanyId, user]);

  const getRiskColor = (score) => {
    if (score >= 9.0) return 'text-red-600';
    if (score >= 7.0) return 'text-orange-600';
    if (score >= 4.0) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Selector de empresa para SuperAdmin */}
      {user?.role === 'superadmin' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
            🔐 Modo SuperAdmin - Filtrar por Empresa
          </h3>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white"
          >
            <option value="">📊 Todas las empresas (consolidado)</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.total_vulnerabilities} vulns)
              </option>
            ))}
          </select>
          {selectedCompanyId && (
            <p className="mt-2 text-sm text-red-600">
              ✓ Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>
            </p>
          )}
          {!selectedCompanyId && (
            <p className="mt-2 text-sm text-red-600">
              📊 Mostrando datos consolidados de todas las empresas
            </p>
          )}
        </div>
      )}
      
      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
        <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
        Dashboard de Riesgo - Análisis Avanzado de Vulnerabilidades
        {user?.role === 'superadmin' && !selectedCompanyId && (
          <span className="ml-2 text-sm font-normal text-gray-500">(Consolidado)</span>
        )}
      </h2>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : riskData ? (
        <div className="space-y-6">
          {/* Risk Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="text-sm font-medium text-gray-700">Total Vulnerabilidades</h3>
              <p className="text-2xl font-bold text-blue-600">{riskData.overview.total_vulnerabilities}</p>
            </div>
            <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border-l-4 border-red-500">
              <h3 className="text-sm font-medium text-gray-700">Críticas</h3>
              <p className="text-2xl font-bold text-red-600">{riskData.overview.critical_vulnerabilities}</p>
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border-l-4 border-orange-500">
              <h3 className="text-sm font-medium text-gray-700">Altas</h3>
              <p className="text-2xl font-bold text-orange-600">{riskData.overview.high_vulnerabilities}</p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="text-sm font-medium text-gray-700">Riesgo Promedio</h3>
              <p className={`text-2xl font-bold ${getRiskColor(riskData.overview.average_risk_score)}`}>
                {riskData.overview.average_risk_score}<span className="text-base font-semibold text-gray-400">/10</span>
              </p>
              <p className="text-[10px] text-gray-500 mt-1 leading-tight">CVSS 0-10 · mayor = peor. No es el CyberScore (0-100) de los informes</p>
            </div>
          </div>

          {/* CVE Coverage and Exploit Risk */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Cobertura CVE</h3>
              <div className="flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full" 
                    style={{width: `${riskData.overview.cve_coverage_percentage}%`}}
                  ></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {riskData.overview.cve_coverage_percentage}%
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Vulnerabilidades con CVE identificado
              </p>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Riesgo de Explotación</h3>
              {(() => {
                const total = riskData.overview.total_vulnerabilities || 0;
                const exploitable = riskData.overview.exploitable_count || 0;
                const pct = riskData.overview.exploit_risk_percentage || 0;
                if (total === 0) {
                  return (
                    <>
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                          <div className="bg-gray-400 h-3 rounded-full" style={{width: '100%'}}></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-500">S/D</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Sin datos suficientes — Ejecutá un escaneo para calcular esta métrica.</p>
                    </>
                  );
                }
                if (exploitable === 0) {
                  return (
                    <>
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                          <div className="bg-emerald-500 h-3 rounded-full" style={{width: '4%'}}></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-emerald-700">0%</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Ninguna de las <b>{total}</b> vulnerabilidades detectadas cuenta con un exploit público conocido (bajo riesgo de ataque automatizado).
                      </p>
                    </>
                  );
                }
                const color = pct >= 60 ? 'bg-red-600' : pct >= 25 ? 'bg-orange-500' : 'bg-yellow-500';
                const textColor = pct >= 60 ? 'text-red-700' : pct >= 25 ? 'text-orange-700' : 'text-yellow-700';
                return (
                  <>
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-3">
                        <div className={`${color} h-3 rounded-full`} style={{width: `${Math.max(pct, 4)}%`}}></div>
                      </div>
                      <span className={`ml-3 text-sm font-semibold ${textColor}`}>{pct}%</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      <b>{exploitable} de {total}</b> vulnerabilidades detectadas tienen exploits públicos disponibles (Metasploit / ExploitDB / PoC).
                    </p>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Recent Scans Risk Summary */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Resumen de Escaneos Recientes</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Tipo</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Objetivo</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Fecha</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-700">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {riskData.recent_scans.map((scan, idx) => {
                    const scanTypeLabels = {
                      'network': 'Red', 'full': 'Completo', 'api_security': 'API Security',
                      'microsegmentation': 'Microsegmentación', 'ai_attack_readiness': 'AI Attack',
                      'ai_bot_security': 'AI Bot', 'dark_web': 'Dark Web',
                      'brand_impersonation': 'Brand Impersonation', 'advanced_web': 'Web Avanzado',
                      'osint': 'OSINT', 'security_posture': 'Postura Seguridad'
                    };
                    return (
                    <tr key={scan.scan_id || idx} className="border-b hover:bg-gray-50">
                      <td className="py-2 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {scanTypeLabels[scan.scan_type] || scan.scan_type || 'Desconocido'}
                        </span>
                      </td>
                      <td className="py-2 text-sm text-gray-900 max-w-xs truncate" title={scan.target}>
                        {scan.target ? (scan.target.length > 30 ? scan.target.substring(0, 30) + '...' : scan.target) : '-'}
                      </td>
                      <td className="py-2 text-sm text-gray-900">
                        {scan.created_at ? _UTC(scan.created_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'}) : '-'}
                      </td>
                      <td className="py-2 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          scan.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {scan.status === 'completed' ? 'Completado' : scan.status || '-'}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Vulnerability Categories and Remediation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Top Categorías de Vulnerabilidades</h3>
              <div className="space-y-2">
                {/* RISK_TOOLTIPS_V1: mostrar todas las categorías, no recortar */}
                {riskData.vulnerability_categories.map((cat, index) => (
                  <div key={cat.category} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 truncate">{cat.category}</span>
                    <span className="text-sm font-medium text-blue-600">{cat.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Resumen de Remediación</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Prioridad Crítica:</span>
                  <span className="text-sm font-medium text-red-600">
                    {riskData.remediation_summary.priority_counts.CRITICAL}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700">Prioridad Alta:</span>
                  <span className="text-sm font-medium text-orange-600">
                    {riskData.remediation_summary.priority_counts.HIGH}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700" title="Suma por severidad de cada vuln: Crítica=8h, Alta=4h, Media=2h, Baja=1h. Estimación estándar de remediación."><span className="cursor-help border-b border-dashed border-gray-400">Horas Estimadas ⓘ</span>:</span>
                  <span className="text-sm font-medium text-blue-600">
                    {riskData.remediation_summary.estimated_total_hours}h
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-700" title="Horas totales / 40 (jornada laboral de 40 h por semana)."><span className="cursor-help border-b border-dashed border-gray-400">Semanas Estimadas ⓘ</span>:</span>
                  <span className="text-sm font-medium text-green-600">
                    {riskData.remediation_summary.estimated_weeks} sem
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Trends */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Tendencias de Riesgo</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-600">Total Escaneos</p>
                <p className="text-lg font-bold text-blue-600">{riskData.risk_trends.total_scans}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Con Críticas</p>
                <p className="text-lg font-bold text-red-600">{riskData.risk_trends.scans_with_criticals}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600" title="improving: 0 críticas | stable: 1-5 críticas | critical: más de 5 críticas"><span className="cursor-help border-b border-dashed border-gray-400">Tendencia ⓘ</span></p>
                <p className="text-lg font-bold text-green-600 capitalize">{riskData.risk_trends.trend_direction}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos de riesgo disponibles</h3>
          <p className="text-gray-500 mb-4">Los datos del dashboard de riesgo aparecerán después de completar escaneos con análisis avanzado</p>
          <p className="text-sm text-blue-600">💡 Inicia un escaneo completo para ver métricas de riesgo detalladas</p>
        </div>
      )}
    </div>
  );
};

// Header Component with User Info and Costillatech.com branding

// ============================================================================
// -------- Helpers UX del módulo VER: tooltips y glosario --------
const VER_GLOSSARY = {
  CVE: "Common Vulnerabilities and Exposures — identificador público único (formato CVE-YYYY-NNNNN) que la comunidad de seguridad asigna a cada vulnerabilidad conocida.",
  KEV: "Known Exploited Vulnerabilities — catálogo oficial de CISA (agencia federal de EE.UU.) que lista los CVEs que ya están siendo explotados activamente por atacantes reales en el mundo. Se actualiza a diario.",
  EPSS: "Exploit Prediction Scoring System — sistema del FIRST.org que estima la probabilidad (0–100%) de que un CVE sea explotado en los próximos 30 días. Complementa el CVSS.",
  CVSS: "Common Vulnerability Scoring System — puntaje de severidad técnico de 0 a 10 asignado a cada CVE (bajo/medio/alto/crítico).",
  SLE: "Single Loss Expectancy — pérdida económica esperada por UN incidente serio. Suma de restitución + interrupción + reputacional. Es el 'peor caso' de un único evento.",
  LEF: "Loss Event Frequency — probabilidad anual (0–95%) de sufrir uno o más incidentes serios. Depende del sector + hallazgos + Threat Intel.",
  ALE: "Annual Loss Expectancy — pérdida económica esperada anual. Fórmula: ALE = SLE × LEF. Es la esperanza matemática de pérdida en un año.",
  FAIR: "Factor Analysis of Information Risk — modelo actuarial estándar (ISO/IEC 27005 compatible) para cuantificar el riesgo cyber en términos económicos.",
  DBIR: "Data Breach Investigations Report — reporte anual de Verizon con estadísticas de miles de incidentes reales; fuente de los benchmarks de LEF sectorial.",
  IBM_COST: "Cost of a Data Breach Report — reporte anual de IBM/Ponemon con el costo por registro comprometido por sector.",
  VER: "Valor Económico del Riesgo — módulo actuarial del producto que cuantifica en pesos argentinos la exposición cyber del cliente combinando FAIR + Threat Intel real.",
  MITIGATION: "Reducción de riesgo estimada al aplicar el plan de remediación propuesto. Aplicado como % de descuento al ALE.",
  CAP_SECTORIAL: "Techo actuarial máximo del SLE por sector: refleja el apetito de riesgo típico del sector. Salud/PyME: 2.0× facturación. Fintech: 5.0×. Cuando el cálculo raw supera este techo, se satura.",
};

const T = ({ term, children }) => {
  const def = VER_GLOSSARY[term];
  if (!def) return <span>{children || term}</span>;
  return (
    <span
      className="underline decoration-dotted decoration-gray-400 cursor-help"
      title={def}
      data-testid={`ver-tooltip-${term.toLowerCase()}`}
    >
      {children || term}
    </span>
  );
};

// ================= VER — Valor Económico del Riesgo (Cliente/User) =================
// ============================================================================
const VerModule = () => {
  const [view, setView] = React.useState('profile');
  const [profile, setProfile] = React.useState({
    annual_revenue: '', operating_margin_pct: '', sector: 'otros',
    critical_records_count: '', critical_ops_dependency_pct: '',
    is_publicly_traded: false, brand_exposure_level: 'media', currency: 'ARS'
  });
  const [sectors, setSectors] = React.useState([]);
  const [scenarios, setScenarios] = React.useState([]);
  const [eligibility, setEligibility] = React.useState(null);
  const [scenarioSelected, setScenarioSelected] = React.useState('ransomware');
  const [results, setResults] = React.useState([]);
  const [multiResults, setMultiResults] = React.useState([]);
  const [lastResult, setLastResult] = React.useState(null);
  const [allScenarios, setAllScenarios] = React.useState(null);
  const [calcAll, setCalcAll] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [calculating, setCalculating] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [threatIntel, setThreatIntel] = React.useState(null);

  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = async () => {
    try {
      const [s, sc, p, e, r, mr, ti] = await Promise.all([
        axios.get(`${API}/risk-valuation/sectors`, auth),
        axios.get(`${API}/risk-valuation/scenarios`, auth),
        axios.get(`${API}/risk-valuation/financial-profile`, auth),
        axios.get(`${API}/risk-valuation/eligibility`, auth),
        axios.get(`${API}/risk-valuation/results`, auth),
        axios.get(`${API}/risk-valuation/multi-results`, auth),
        axios.get(`${API}/threat-intel/enrich-client`, auth).catch(() => ({ data: null })),
      ]);
      setSectors(s.data); setScenarios(sc.data);
      if (p.data) setProfile({ ...profile, ...p.data });
      setEligibility(e.data); setResults(r.data); setMultiResults(mr.data);
      if (ti && ti.data) setThreatIntel(ti.data);
    } catch (e) { console.error(e); setError('Error cargando VER'); }
  };

  React.useEffect(() => { loadAll(); }, []);

  const fmtARS = (n) => {
    if (!n && n !== 0) return '-';
    return 'ARS ' + Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const saveProfile = async () => {
    setSaving(true); setError(null);
    try {
      const payload = { ...profile,
        annual_revenue: Number(profile.annual_revenue),
        operating_margin_pct: Number(profile.operating_margin_pct),
        critical_records_count: profile.critical_records_count === '' ? null : Number(profile.critical_records_count),
        critical_ops_dependency_pct: profile.critical_ops_dependency_pct === '' ? null : Number(profile.critical_ops_dependency_pct),
      };
      await axios.post(`${API}/risk-valuation/financial-profile`, payload, auth);
      const e = await axios.get(`${API}/risk-valuation/eligibility`, auth);
      setEligibility(e.data);
      alert('✅ Perfil financiero guardado');
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const calculate = async () => {
    setCalculating(true); setError(null);
    try {
      const res = await axios.post(`${API}/risk-valuation/calculate`, { scenario: scenarioSelected }, auth);
      setLastResult(res.data);
      const r = await axios.get(`${API}/risk-valuation/results`, auth);
      setResults(r.data);
      setView('results');
    } catch (e) { setError(e.response?.data?.detail?.error || e.response?.data?.detail || e.message); }
    finally { setCalculating(false); }
  };

  const calculateAll = async () => {
    setCalcAll(true); setError(null);
    try {
      const res = await axios.post(`${API}/risk-valuation/calculate-all`, {}, auth);
      setAllScenarios(res.data);
      setLastResult(null);
      try { const mr = await axios.get(`${API}/risk-valuation/multi-results`, auth); setMultiResults(mr.data); } catch(e){}
      setView('results');
    } catch (e) { setError(e.response?.data?.detail?.missing || e.response?.data?.detail?.error || e.message); }
    finally { setCalcAll(false); }
  };

  const downloadPdf = async (resultId, isMulti=false) => {
    try {
      const url_path = isMulti ? `risk-valuation/multi-results/${resultId}/pdf` : `risk-valuation/results/${resultId}/pdf`;
      const res = await axios.get(`${API}/${url_path}`, { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      // El backend ya manda el nombre con la marca del entorno en Content-Disposition
      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'] || '';
      const cdMatch = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const brandSlug = (process.env.REACT_APP_BRAND_NAME || 'seguria').toLowerCase().replace(/[^a-z0-9]+/g, '');
      a.download = cdMatch
        ? decodeURIComponent(cdMatch[1])
        : `${brandSlug}_reporte_ver_${resultId.substring(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Error descargando PDF: ' + (e.response?.data?.detail || e.message)); }
  };

  const ElgBadge = ({ ok, label }) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid={`ver-badge-${label.toLowerCase().replace(/\s/g,'-')}`}>
      {ok ? '✅' : '⚠️'} {label}
    </span>
  );

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8" data-testid="ver-module">
      <div className="px-4 sm:px-0">
        <h2 className="text-2xl font-bold text-gray-900">Valor Económico del Riesgo (VER)</h2>
        <p className="text-sm text-gray-600 mt-1">Cuantifica el impacto económico potencial de un incidente cibernético para tu organización, con base en tus escaneos y tu perfil financiero.</p>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3 mt-4" data-testid="ver-error">{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

        {/* Sub-tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {[
              { k: 'profile', l: '1. Perfil Financiero' },
              { k: 'calculate', l: '2. Calcular VER' },
              { k: 'results', l: '3. Resultados' },
            ].map(t => (
              <button key={t.k} onClick={() => setView(t.k)} data-testid={`ver-subtab-${t.k}`}
                className={`py-3 px-1 border-b-2 text-sm font-medium ${view === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {t.l}
              </button>
            ))}
          </nav>
        </div>

        {/* --- View: Perfil Financiero --- */}
        {view === 'profile' && (
          <div className="bg-white shadow rounded-lg p-6 mt-6" data-testid="ver-profile-view">
            <h3 className="text-lg font-semibold text-gray-800">Datos financieros de la organización</h3>
            <p className="text-xs text-gray-500 mt-1">Los primeros 3 campos son obligatorios para el cálculo.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div>
                <label className="text-sm text-gray-700">Facturación anual (ARS) *</label>
                <input type="number" value={profile.annual_revenue} onChange={e => setProfile({...profile, annual_revenue: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded" placeholder="Ej: 50000000" data-testid="ver-annual-revenue" />
              </div>
              <div>
                <label className="text-sm text-gray-700">Margen operativo (%) *</label>
                <input type="number" step="0.1" value={profile.operating_margin_pct} onChange={e => setProfile({...profile, operating_margin_pct: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded" placeholder="Ej: 15" data-testid="ver-op-margin" />
              </div>
              <div>
                <label className="text-sm text-gray-700 flex items-center justify-between">
                  <span>Sector *</span>
                  <span className="text-xs text-gray-500 font-normal italic">Impacta LEF base, cap sectorial y costo/registro</span>
                </label>
                <select value={profile.sector} onChange={e => setProfile({...profile, sector: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded" data-testid="ver-sector">
                  {sectors.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-700">Registros/clientes en bases críticas</label>
                <input type="number" value={profile.critical_records_count} onChange={e => setProfile({...profile, critical_records_count: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded" placeholder="Ej: 20000" data-testid="ver-records" />
              </div>
              <div>
                <label className="text-sm text-gray-700">% de operación dependiente de sistemas críticos</label>
                <input type="number" step="1" min="0" max="100" value={profile.critical_ops_dependency_pct} onChange={e => setProfile({...profile, critical_ops_dependency_pct: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded" placeholder="Ej: 70" data-testid="ver-dependency" />
              </div>
              <div>
                <label className="text-sm text-gray-700">Perfil de marca / exposición</label>
                <select value={profile.brand_exposure_level} onChange={e => setProfile({...profile, brand_exposure_level: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded" data-testid="ver-brand">
                  <option value="alta">Alta (B2C con exposición pública)</option>
                  <option value="media">Media</option>
                  <option value="baja">Baja (B2B bajo perfil)</option>
                </select>
              </div>
              <div className="flex items-center mt-6">
                <input type="checkbox" id="ver-listed" checked={!!profile.is_publicly_traded} onChange={e => setProfile({...profile, is_publicly_traded: e.target.checked})} className="mr-2" data-testid="ver-listed" />
                <label htmlFor="ver-listed" className="text-sm text-gray-700">La empresa cotiza en bolsa</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={saveProfile} disabled={saving} data-testid="ver-save-profile"
                className={`px-4 py-2 rounded-md text-sm font-semibold text-white ${saving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {saving ? 'Guardando…' : 'Guardar perfil'}
              </button>
            </div>
          </div>
        )}

        {/* --- View: Calcular --- */}
        {view === 'calculate' && (
          <div className="bg-white shadow rounded-lg p-6 mt-6" data-testid="ver-calc-view">
            <h3 className="text-lg font-semibold text-gray-800">Estado y cálculo</h3>
            {eligibility && (
              <>
                <div className="flex flex-wrap gap-2 mt-4">
                  <ElgBadge ok={eligibility.profile_complete} label="Perfil financiero" />
                  <ElgBadge ok={eligibility.evidence_sufficient} label="Evidencia de escaneos" />
                </div>
                {eligibility.missing?.length > 0 && (
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-900" data-testid="ver-missing">
                    <div className="font-semibold mb-1">Requisitos pendientes:</div>
                    <ul className="list-disc list-inside space-y-1">{eligibility.missing.map((m,i) => <li key={i}>{m}</li>)}</ul>
                  </div>
                )}
                {threatIntel && threatIntel.total_cves > 0 && (
                  <div className="mt-6 border-2 border-red-200 rounded-lg p-4 bg-gradient-to-br from-red-50 to-orange-50" data-testid="ver-threat-intel-panel">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-red-900 uppercase tracking-wide">🎯 Contexto de amenazas reales</h4>
                      <span className="text-xs text-gray-600"><T term="KEV">CISA KEV</T> + <T term="EPSS">FIRST.org EPSS</T></span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white rounded p-3 border border-red-100">
                        <div className="text-xs text-gray-600"><T term="CVE">CVEs</T> analizados</div>
                        <div className="text-2xl font-bold text-gray-900">{threatIntel.total_cves}</div>
                      </div>
                      <div className="bg-white rounded p-3 border border-red-300" data-testid="ver-kev-hits">
                        <div className="text-xs text-red-700"><T term="CVE">CVEs</T> <T term="KEV">KEV</T> activos</div>
                        <div className="text-2xl font-bold text-red-700">{threatIntel.multipliers?.kev_hits || 0}</div>
                      </div>
                      <div className="bg-white rounded p-3 border border-yellow-300">
                        <div className="text-xs text-yellow-700"><T term="EPSS">EPSS</T> pct promedio</div>
                        <div className="text-2xl font-bold text-yellow-800">{((threatIntel.multipliers?.epss_avg_percentile || 0) * 100).toFixed(1)}%</div>
                      </div>
                      <div className="bg-white rounded p-3 border border-purple-300" data-testid="ver-ti-multiplier">
                        <div className="text-xs text-purple-700">Ajuste sobre <T term="LEF">LEF</T></div>
                        <div className="text-2xl font-bold text-purple-800">×{threatIntel.multipliers?.combined_multiplier || 1.0}</div>
                      </div>
                    </div>
                    {(threatIntel.top_kev || []).length > 0 && (
                      <div className="mt-3 text-xs">
                        <div className="font-semibold text-gray-700 mb-1">Top CVEs activamente explotados en su superficie:</div>
                        <ul className="space-y-1">
                          {threatIntel.top_kev.slice(0, 3).map((k, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="font-mono text-red-700 font-bold">{k.cve_id}</span>
                              <span className="text-gray-700">{(k.kev_short_description || '').substring(0, 100)}...</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-3 italic">
                      Estos multiplicadores impactan la <T term="LEF"><b>LEF</b></T> (frecuencia anual de incidente) del cálculo <T term="VER">VER</T> — pueden aumentar el <T term="ALE">ALE</T> hasta ×2.5.
                    </div>
                  </div>
                )}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-700">Escenario a evaluar</label>
                    <select value={scenarioSelected} onChange={e => setScenarioSelected(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded" data-testid="ver-scenario-select">
                      {scenarios.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end gap-2">
                    <button onClick={calculate} disabled={!eligibility.can_calculate || calculating || calcAll} data-testid="ver-calc-btn"
                      title={!eligibility.can_calculate ? 'Completá los requisitos pendientes arriba' : ''}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold text-white ${(!eligibility.can_calculate || calculating || calcAll) ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}>
                      {calculating ? 'Calculando…' : 'Calcular UN escenario'}
                    </button>
                    <button onClick={calculateAll} disabled={!eligibility.can_calculate || calculating || calcAll} data-testid="ver-calc-all-btn"
                      title={!eligibility.can_calculate ? 'Completá los requisitos pendientes arriba' : 'Calcula los 5 escenarios y devuelve una comparativa'}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold text-white ${(!eligibility.can_calculate || calculating || calcAll) ? 'bg-gray-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}>
                      {calcAll ? 'Calculando todos…' : '⚡ Calcular TODOS los escenarios'}
                    </button>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Probabilidad estimada actual: <b>{eligibility.evidence?.total_vulnerabilities_historical || 0} vulnerabilidades históricas · {eligibility.evidence?.total_scans_90d || 0} escaneos en 90d{eligibility.evidence?.kev_exposure_count > 0 ? ` · ${eligibility.evidence.kev_exposure_count} CVEs KEV activos` : ''}</b>
                </div>
              </>
            )}
          </div>
        )}

        {/* --- View: Results --- */}
        {view === 'results' && (
          <div className="mt-6 space-y-4">
            {/* Vista Multi-Escenario */}
            {allScenarios && (
              <div className="bg-white shadow rounded-lg p-6" data-testid="ver-all-scenarios">
                <h3 className="text-lg font-semibold text-gray-800">Comparativa de los 5 escenarios</h3>
                <div className="info mt-2 text-xs bg-blue-50 border border-blue-200 rounded p-3 text-blue-900">
                  <b>Los escenarios NO se suman.</b> Cada uno responde: "¿cuánto perdería SI sufro este tipo de incidente?".
                  El agregado inferior asume independencia estadística (aproximación conservadora — en la práctica un phishing suele derivar en ransomware).
                </div>
                <table className="min-w-full text-sm mt-3">
                  <thead><tr className="text-left border-b">
                    <th className="py-2">Escenario</th>
                    <th className="text-right" title="SLE bruto sin caps aplicados">SLE bruto</th>
                    <th className="text-right" title="SLE final con caps componentes + sectorial">SLE final</th>
                    <th className="text-right">LEF</th>
                    <th className="text-right">ALE (anual)</th>
                    <th className="text-right">ALE c/remediación</th>
                    <th className="text-right">Ahorro</th>
                  </tr></thead>
                  <tbody>
                    {allScenarios.scenarios.filter(s => !s.error).map(s => (
                      <tr key={s.scenario} className="border-b hover:bg-gray-50">
                        <td className="py-2 capitalize font-medium">{s.scenario.replace('_',' ')} {s.scenario === allScenarios.aggregate.worst_case_scenario && <span className="ml-1 text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded">peor caso</span>}</td>
                        <td className="text-gray-600 text-xs text-right">{fmtARS(s.sle_raw_uncapped)}</td>
                        <td className="font-semibold text-right">{fmtARS(s.sle)} {s.sle_capped && <span className="ml-1 text-[10px] text-orange-700">(capado)</span>}</td>
                        <td className="text-right">{(s.lef_annual*100).toFixed(1)}%</td>
                        <td className="text-red-700 text-right">{fmtARS(s.ale_annual)}</td>
                        <td className="text-green-700 text-right">{fmtARS(s.ale_with_seguria)}</td>
                        <td className="text-blue-700 text-right">{fmtARS(s.ale_savings)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 font-bold bg-gray-50">
                      <td className="py-2">TOTAL AGREGADO*</td><td></td><td></td><td></td>
                      <td className="text-red-800 text-right">{fmtARS(allScenarios.aggregate.ale_annual_total)}</td>
                      <td className="text-green-800 text-right">{fmtARS(allScenarios.aggregate.ale_with_seguria_total)}</td>
                      <td className="text-blue-800 text-right">{fmtARS(allScenarios.aggregate.ale_savings_total)}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="mt-2 text-[11px] text-gray-500">* {allScenarios.aggregate.note}</div>
                <div className="mt-4 flex justify-end">
                  <button onClick={() => downloadPdf(allScenarios.id, true)}
                    className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-purple-700 hover:bg-purple-800"
                    data-testid="ver-download-multi-pdf">
                    📄 Descargar reporte multi-escenario (PDF)
                  </button>
                </div>
              </div>
            )}

            {multiResults && multiResults.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6" data-testid="ver-multi-history">
                <h3 className="text-lg font-semibold text-gray-800">Histórico multi-escenario</h3>
                <table className="min-w-full text-sm mt-3">
                  <thead><tr className="text-left border-b"><th className="py-2">Fecha</th><th>Peor caso</th><th className="text-right">ALE agregado</th><th className="text-right">Ahorro con Costillatech.com</th><th></th></tr></thead>
                  <tbody>
                    {multiResults.map(mr => (
                      <tr key={mr.id} className="border-b hover:bg-gray-50" data-testid={`ver-multi-row-${mr.id}`}>
                        <td className="py-2">{new Date(mr.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</td>
                        <td className="capitalize">{(mr.aggregate?.worst_case_scenario||'-').replace('_',' ')}</td>
                        <td className="font-semibold text-red-700 text-right">{fmtARS(mr.aggregate?.ale_annual_total)}</td>
                        <td className="text-green-700 text-right">{fmtARS(mr.aggregate?.ale_savings_total)}</td>
                        <td><button onClick={() => downloadPdf(mr.id, true)} className="text-purple-700 hover:underline text-xs">📄 PDF</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {lastResult && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 shadow rounded-lg p-6" data-testid="ver-result-card">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Escenario</div>
                    <div className="font-semibold text-gray-800">{lastResult.scenario.replace('_',' ').toUpperCase()}</div>
                  </div>
                  <div className="text-right" title="SLE (Single Loss Expectancy): impacto económico esperado ante UN incidente serio de este tipo.">
                    <div className="text-xs text-gray-500 uppercase cursor-help border-b border-dashed border-gray-400">SLE — Impacto de UN incidente ⓘ</div>
                    <div className="text-3xl font-bold text-red-700" data-testid="ver-total">{fmtARS(lastResult.breakdown.sle ?? lastResult.breakdown.total)}</div>
                    {lastResult.breakdown.sle_capped && <div className="text-[10px] text-orange-700 mt-1">Capado por regla de 3× facturación</div>}
                  </div>
                </div>
                <hr className="my-4"/>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div><div className="text-gray-500">Restitución</div><div className="font-semibold">{fmtARS(lastResult.breakdown.restitution)}</div></div>
                  <div><div className="text-gray-500">Interrupción de negocio</div><div className="font-semibold">{fmtARS(lastResult.breakdown.interruption)}</div></div>
                  <div><div className="text-gray-500">Reputacional</div><div className="font-semibold">{fmtARS(lastResult.breakdown.reputational)}</div></div>
                </div>

                <div className="mt-5 bg-white rounded-lg p-4 border">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-xs text-gray-500 uppercase tracking-wide cursor-help border-b border-dashed border-gray-400"
                         title="ALE (Annual Loss Expectancy) = SLE × LEF (frecuencia anual esperada). Es la esperanza matemática anual de pérdida — el número que un CFO usaría para calcular ROI de seguridad.">
                      ALE — Esperanza de pérdida anual ⓘ
                    </div>
                    <div className="text-xs text-gray-500">LEF anual: <b>{((lastResult.breakdown.lef_annual ?? 0) * 100).toFixed(1)}%</b></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Sin remediación</div>
                      <div className="text-xl font-bold text-red-700">{fmtARS(lastResult.breakdown.ale_annual)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Con remediación aplicada</div>
                      <div className="text-xl font-bold text-green-700">{fmtARS(lastResult.breakdown.ale_with_seguria)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Ahorro anual</div>
                      <div className="text-xl font-bold text-blue-700">{fmtARS(lastResult.breakdown.ale_savings)}</div>
                    </div>
                  </div>
                </div>

                {/* Reglas actuariales aplicadas — transparencia total */}
                <div className="mt-5 bg-white rounded-lg p-4 border" data-testid="ver-actuarial-rules">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Reglas actuariales aplicadas en este cálculo</div>
                  {(() => {
                    const t = lastResult.calculation_trace || {};
                    const b = lastResult.breakdown || {};
                    const rev = t.profile_snapshot?.annual_revenue || 0;
                    const capSec = (t.sle_cap_ratio_sector || 3) * rev;
                    const capRest = ((t.component_caps?.max_restitution_x_rev) || 2) * rev;
                    const capInt = ((t.component_caps?.max_interruption_x_rev) || 1.5) * rev;
                    const capRep = ((t.component_caps?.max_reputational_x_rev) || 1) * rev;
                    const wasCapped = t.sle_capped_by_revenue_rule;
                    const restCapped = b.restitution >= capRest * 0.999;
                    const intCapped = b.interruption >= capInt * 0.999;
                    const repCapped = b.reputational >= capRep * 0.999;
                    const Row = ({label, formula, value, applied}) => (
                      <div className={`flex items-start justify-between text-xs py-2 border-b border-gray-100 ${applied ? 'bg-orange-50' : ''}`}>
                        <div>
                          <div className="font-medium text-gray-800">{label} {applied && <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-200 text-orange-900 text-[10px] font-bold">APLICADO</span>}</div>
                          <div className="text-gray-500">{formula}</div>
                        </div>
                        <div className="font-mono text-gray-700 text-right whitespace-nowrap ml-3">{value}</div>
                      </div>
                    );
                    return (
                      <div>
                        <Row label="Size multiplier" applied={false}
                             formula={`Factor por tamaño de empresa (facturación ARS ${rev.toLocaleString('es-AR')})`}
                             value={`× ${t.size_multiplier}`} />
                        <Row label="LEF anual (frecuencia)" applied={false}
                             formula={`base ${t.sector_benchmark?.lef_base} × vuln_mult ${t.vuln_multiplier} → cap [3%, 95%]`}
                             value={`${((t.lef_annual || 0)*100).toFixed(1)}%`} />
                        <Row label="Cap restitución" applied={restCapped}
                             formula={`≤ ${t.component_caps?.max_restitution_x_rev || 2}× facturación`}
                             value={fmtARS(capRest)} />
                        <Row label="Cap interrupción" applied={intCapped}
                             formula={`≤ ${t.component_caps?.max_interruption_x_rev || 1.5}× facturación`}
                             value={fmtARS(capInt)} />
                        <Row label="Cap reputacional" applied={repCapped}
                             formula={`≤ ${t.component_caps?.max_reputational_x_rev || 1}× facturación`}
                             value={fmtARS(capRep)} />
                        <Row label={`Cap sectorial (${t.sector_used})`} applied={wasCapped}
                             formula={`≤ ${t.sle_cap_ratio_sector}× facturación — apetito de riesgo del sector`}
                             value={fmtARS(capSec)} />
                        <div className="mt-3 pt-2 flex justify-between text-xs">
                          <span className="text-gray-500">SLE bruto (sin caps):</span>
                          <span className="font-mono font-semibold">{fmtARS(t.sle_raw_uncapped || b.sle)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">SLE final (con caps):</span>
                          <span className="font-mono font-bold text-red-700">{fmtARS(b.sle)}</span>
                        </div>
                        {wasCapped && <div className="mt-3 text-sm text-orange-900 bg-orange-100 border-2 border-orange-300 rounded p-3" data-testid="ver-sle-capped-warning"><b>⚠ El <T term="SLE">SLE</T> quedó saturado por el <T term="CAP_SECTORIAL">cap sectorial</T> ({t.sle_cap_ratio_sector}× facturación).</b><div className="mt-2">Cambios en <b>brand/cotización en bolsa</b> pueden no verse reflejados en el SLE cuando ya está en este techo — pero <b>SÍ impactan el <T term="ALE">ALE</T> a través de la <T term="LEF">LEF</T></b> (que también cambia por sector). Para ver la sensibilidad completa mirá el ALE.</div></div>}
                      </div>
                    );
                  })()}
                </div>

                <div className="mt-4 flex justify-end">
                  <button onClick={() => downloadPdf(lastResult.id)}
                    className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-purple-700 hover:bg-purple-800"
                    data-testid="ver-download-pdf">
                    📄 Descargar reporte PDF
                  </button>
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Modelo: FAIR simplificado (SLE + LEF → ALE) · Size mult: <b>{lastResult.calculation_trace?.size_multiplier}</b> · Registros afectados est: <b>{Number(lastResult.calculation_trace?.records_affected_estimate ?? 0).toLocaleString('es-AR')}</b> · SLE bruto (sin cap): <b>{fmtARS(lastResult.calculation_trace?.sle_raw_uncapped ?? lastResult.breakdown.sle)}</b> · Cap sector: <b>{lastResult.calculation_trace?.sle_cap_ratio_sector}× rev</b> · benchmark <b>{lastResult.calculation_trace?.benchmark_version}</b> · {new Date(lastResult.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}
                </div>
              </div>
            )}

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800">Histórico de cálculos</h3>
              {results.length === 0 ? (
                <div className="text-sm text-gray-500 mt-3">No hay cálculos previos. Configurá el perfil y calculá tu primer VER.</div>
              ) : (
                <table className="min-w-full text-sm mt-3" data-testid="ver-results-table">
                  <thead><tr className="text-left border-b"><th className="py-2">Fecha</th><th>Escenario</th><th className="text-right">SLE</th><th className="text-right">ALE (anual)</th><th className="text-right">ALE c/remediación</th><th className="text-right">LEF</th><th></th></tr></thead>
                  <tbody>
                    {results.map(r => (
                      <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`ver-result-row-${r.id}`}>
                        <td className="py-2">{new Date(r.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</td>
                        <td className="capitalize">{r.scenario.replace('_',' ')}</td>
                        <td className="font-semibold text-right" title="SLE — impacto de un evento">{fmtARS(r.breakdown.sle ?? r.breakdown.total)}</td>
                        <td className="text-red-700 text-right" title="ALE anual sin remediación">{fmtARS(r.breakdown.ale_annual)}</td>
                        <td className="text-green-700 text-right" title="ALE con remediación aplicada">{fmtARS(r.breakdown.ale_with_seguria)}</td>
                        <td className="text-right">{((r.breakdown.lef_annual ?? 0) * 100).toFixed(1)}%</td>
                        <td><button onClick={() => downloadPdf(r.id)} className="text-purple-700 hover:underline text-xs" data-testid={`ver-pdf-row-${r.id}`}>📄 PDF</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
// ============================================================================
// =================== END VER Module =========================================
// ============================================================================


const Header = ({ setActiveTab }) => {
  const { user, company, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="h-10 w-auto mr-3" />
            <div className="text-2xl font-bold text-gray-900">Costillatech.com</div>
            <div className="ml-4 text-sm text-gray-600">Plataforma de Auditoría de Seguridad Ética</div>
            {user?.role === 'superadmin' && (
              <div className="ml-4 px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded">
                SUPER ADMIN
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">
              ISO 27001 | OWASP | NIST
            </div>
            <span
              className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold"
              data-testid="user-menu-role"
              title={ROLE_CATALOG.find((r) => r.value === String(user?.role || '').toLowerCase())?.summary
                || 'Administra toda la plataforma Costillatech.com'}
            >
              {roleLabel(user)}
            </span>
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
                data-testid="user-menu-btn"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  user?.role === 'superadmin' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                }`}>
                  <span className="font-medium">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-gray-500">{company?.name}</p>
                </div>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                  <div className="px-4 py-2 border-b">
                    <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <p className="text-xs text-gray-400">{company?.name}</p>
                    <p className={`text-xs font-medium ${
                      user?.role === 'superadmin' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {user?.role === 'superadmin' ? 'Super Admin' : `Plan: ${company?.subscription_plan?.toUpperCase()}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-testid="user-menu-role-detail"
                       title={ROLE_CATALOG.find((r) => r.value === String(user?.role || '').toLowerCase())?.summary || ''}>
                      Rol: <span className="font-semibold text-gray-700">{roleLabel(user)}</span>
                    </p>
                  </div>
                  {isAdmin(user) && (
                    <button
                      onClick={() => {
                        setActiveTab('users');
                        setShowUserMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      data-testid="menu-users-btn"
                    >
                      👥 Usuarios y Roles
                    </button>
                  )}
                  {isAdmin(user) && (
                    <button
                      onClick={() => {
                        setActiveTab('authorized-targets');
                        setShowUserMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      data-testid="menu-authorized-targets-btn"
                    >
                      ⚖️ Dominios Autorizados
                    </button>
                  )}
                  <a
                    href="/downloads/Novedades-v3.0.0.docx"
                    download
                    onClick={() => setShowUserMenu(false)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t"
                    data-testid="menu-change-report-btn"
                  >
                    📄 Novedades v3.0.0 (Word)
                  </a>
                  <button
                    onClick={logout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

 

// Aviso de permisos insuficientes segun el rol (v3.0.0)
const RoleBlocked = ({ user, action, required }) => (
  <div className="bg-white rounded-lg shadow-lg p-8 text-center" data-testid="role-blocked">
    <div className="text-5xl mb-4">🔒</div>
    <h2 className="text-xl font-bold text-gray-800 mb-2">No tenés permiso para {action}</h2>
    <p className="text-gray-600 max-w-2xl mx-auto">
      Tu rol actual es <strong>{roleLabel(user)}</strong> y esta acción requiere el rol{' '}
      <strong>{required}</strong> o superior. Pedile a un administrador de tu empresa que te cambie el rol
      desde <em>Usuarios y Roles</em>.
    </p>
    <div className="mt-6 text-left max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-3">
      {ROLE_CATALOG.map((r) => (
        <div key={r.value} className="border border-gray-200 rounded-lg p-3">
          <p className="font-semibold text-gray-900 text-sm">{r.label}</p>
          <p className="text-xs text-gray-600 mt-1">{r.summary}</p>
        </div>
      ))}
    </div>
  </div>
);

// Plan Management Component for SuperAdmin
const PlanManager = ({ refreshTrigger = 0, onPlanChange = () => {} }) => {
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    plan_code: 'trial',
    name: '',
    description: '',
    price_usd: 0,
    price_ars: 0,
    price_eur: 0,
    default_currency: 'usd',
    targets_limit: 5,
    scans_per_month: 10,
    storage_gb: 5,
    api_calls_limit: 1000,
    trial_duration_days: 0,
    features: []
  });
  const [loading, setLoading] = useState(false);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/admin/plans`, { headers });
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [refreshTrigger]); // Refresh when trigger changes

  const resetForm = () => {
    setFormData({
      plan_code: 'trial',
      name: '',
      description: '',
      price_usd: 0,
      price_ars: 0,
      price_eur: 0,
      default_currency: 'usd',
      targets_limit: 5,
      scans_per_month: 10,
      storage_gb: 5,
      api_calls_limit: 1000,
      trial_duration_days: 0,
      features: []
    });
    setEditingPlan(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingPlan) {
        // Update existing plan - ensure plan_code is preserved and properly formatted
        const updateData = { 
          ...formData, 
          plan_code: editingPlan.plan_code.toLowerCase() // Preserve original plan_code in lowercase
        };
        console.log('Updating plan with data:', updateData);
        console.log('Using headers:', headers);
        await axios.put(`${API}/admin/plans/${editingPlan.id}`, updateData, { headers });
        alert('Plan actualizado exitosamente');
      } else {
        // Create new plan - convert plan_code to lowercase
        const createData = { ...formData, plan_code: formData.plan_code.toLowerCase() };
        await axios.post(`${API}/admin/plans`, createData, { headers });
        alert('Plan creado exitosamente');
      }
      
      resetForm();
      await fetchPlans(); // Ensure refresh completes
      onPlanChange(); // Notify parent component
    } catch (error) {
      console.error("Plan operation error:", error);
      console.error("Error response:", error.response);
      console.error("Form data sent:", editingPlan ? { ...formData, plan_code: editingPlan.plan_code.toLowerCase() } : { ...formData, plan_code: formData.plan_code.toLowerCase() });
      
      let errorMessage = "Error desconocido";
      
      if (error.response?.status === 422 || error.response?.status === 400) {
        errorMessage = "Error de validación de datos. ";
        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMessage += error.response.data.detail.map(err => 
              `${err.loc[err.loc.length-1]}: ${err.msg}`
            ).join(", ");
          } else {
            errorMessage += error.response.data.detail;
          }
        }
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = "Error de autenticación. Por favor, inicie sesión nuevamente.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert("Error: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    console.log('🔧 Editing plan:', plan); // Debug log
    console.log('🔧 Plan code from server:', plan.plan_code); // Specific debug for plan_code
    setEditingPlan(plan);
    
    const newFormData = {
      plan_code: (plan.plan_code || 'trial').toLowerCase(),
      name: plan.name || '',
      description: plan.description || '',
      price_usd: Number(plan.price_usd) || 0,
      price_ars: Number(plan.price_ars) || 0,
      price_eur: Number(plan.price_eur) || 0,
      default_currency: plan.default_currency || 'usd',
      targets_limit: Number(plan.targets_limit) || 5,
      scans_per_month: Number(plan.scans_per_month) || 10,
      storage_gb: Number(plan.storage_gb) || 5,
      api_calls_limit: Number(plan.api_calls_limit) || 1000,
      trial_duration_days: Number(plan.trial_duration_days) || 0,
      features: plan.features || []
    };
    
    console.log('🔧 Setting form data:', newFormData); // Debug log
    setFormData(newFormData);
    setShowForm(true);
  };

  const handleDelete = async (planId) => {
    if (window.confirm("¿Estás seguro de eliminar este plan?")) {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/admin/plans/${planId}`, { headers });
        await fetchPlans();
        onPlanChange(); // Notify parent component
        alert('Plan eliminado exitosamente');
      } catch (error) {
        alert("Error eliminando plan: " + (error.response?.data?.detail || error.message));
      }
    }
  };

  const getPlanColor = (plan_code) => {
    switch (plan_code) {
      case 'TRIAL': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'BASIC': return 'bg-green-100 text-green-800 border-green-200';
      case 'PROFESSIONAL': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ENTERPRISE': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'ENTERPRISE_EXCLUSIVO': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
          Gestión de Planes de Suscripción Anual
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Crear Plan
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Código del Plan</label>
              <select
                value={formData.plan_code?.toLowerCase()}
                onChange={(e) => setFormData({...formData, plan_code: e.target.value.toLowerCase()})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                disabled={editingPlan}
              >
                <option value="trial">TRIAL</option>
                <option value="basic">BASIC</option>
                <option value="professional">PROFESSIONAL</option>
                <option value="enterprise">ENTERPRISE</option>
                <option value="enterprise_exclusivo">ENTERPRISE EXCLUSIVO</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Plan</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="Plan Básico"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                rows="2"
                placeholder="Descripción del plan..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Precio USD ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_usd}
                onChange={(e) => setFormData({...formData, price_usd: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Precio ARS ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_ars}
                onChange={(e) => setFormData({...formData, price_ars: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Precio EUR (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_eur}
                onChange={(e) => setFormData({...formData, price_eur: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Límite de Objetivos</label>
              <input
                type="number"
                value={formData.targets_limit}
                onChange={(e) => setFormData({...formData, targets_limit: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="-1 para ilimitado"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Escaneos por Mes</label>
              <input
                type="number"
                value={formData.scans_per_month}
                onChange={(e) => setFormData({...formData, scans_per_month: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="-1 para ilimitado"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Almacenamiento (GB)</label>
              <input
                type="number"
                value={formData.storage_gb}
                onChange={(e) => setFormData({...formData, storage_gb: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Moneda por Defecto</label>
              <select
                value={formData.default_currency}
                onChange={(e) => setFormData({...formData, default_currency: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                <option value="usd">USD</option>
                <option value="ars">ARS</option>
                <option value="eur">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duración Trial (Días)</label>
              <input
                type="number"
                value={formData.trial_duration_days}
                onChange={(e) => setFormData({...formData, trial_duration_days: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="0 para planes de pago"
              />
            </div>
          </div>
          
          <div className="mt-4 flex space-x-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? "Guardando..." : editingPlan ? "Actualizar Plan" : "Crear Plan"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {plans.map((plan) => (
          <div key={plan.id} className={`border rounded-lg p-4 hover:bg-gray-50 ${getPlanColor(plan.plan_code)}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <h3 className="font-semibold text-gray-900 mr-3">{plan.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPlanColor(plan.plan_code)}`}>
                    {plan.plan_code?.toUpperCase()}
                  </span>
                  {!plan.is_active && (
                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      INACTIVO
                    </span>
                  )}
                </div>
                
                <p className="text-gray-700 mb-3">{plan.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-600">Precio USD:</p>
                    <p className="text-green-600 font-semibold">${plan.price_usd}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">Precio ARS:</p>
                    <p className="text-green-600 font-semibold">${plan.price_ars}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">Objetivos:</p>
                    <p className="text-blue-600 font-semibold">{plan.targets_limit === -1 ? 'Ilimitado' : plan.targets_limit}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">Escaneos/Mes:</p>
                    <p className="text-purple-600 font-semibold">{plan.scans_per_month === -1 ? 'Ilimitado' : plan.scans_per_month}</p>
                  </div>
                </div>
                
                <div className="mt-2 text-xs text-gray-500">
                  Creado: {_UTC(plan.created_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})} | 
                  Actualizado: {_UTC(plan.updated_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}
                </div>
              </div>
              
              <div className="flex space-x-2 ml-4">
                <button
                  onClick={() => handleEdit(plan)}
                  className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                  title="Editar Plan"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(plan.id)}
                  className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                  title="Eliminar Plan"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {plans.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay planes configurados</h3>
            <p className="text-gray-500 mb-4">Crea el primer plan de suscripción para la plataforma</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Crear Primer Plan
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// Module Toggle Admin Component
const ModulesAdmin = ({ companies, fetchCompanies }) => {
  const [moduleStates, setModuleStates] = React.useState({});
  const [loadingModules, setLoadingModules] = React.useState(true);
  const [mpEnabled, setMpEnabled] = React.useState(true);
  const [mpEnvAllowed, setMpEnvAllowed] = React.useState(true);
  const [wipingDemo, setWipingDemo] = React.useState(false);
  const [showWipeConfirm, setShowWipeConfirm] = React.useState(false);

  const isDemoOrDevEnv = (typeof window !== "undefined")
    && /^(dev|demo)\./i.test(window.location.hostname);

  const handleWipeDemo = async () => {
    setShowWipeConfirm(false);
    setWipingDemo(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/system/admin/wipe-demo-database`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ Base vaciada. ${res.data.total_deleted} documentos eliminados. Se preserva ${res.data.preserved_admin}.`);
      window.location.reload();
    } catch (err) {
      console.error("Wipe demo error:", err);
      alert("❌ Error al vaciar la base: " + (err.response?.data?.detail || err.message));
    } finally {
      setWipingDemo(false);
    }
  };

  const fetchModuleStates = React.useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/all-company-modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuleStates(res.data || {});
    } catch (err) {
      console.error("Error fetching modules:", err);
    } finally {
      setLoadingModules(false);
    }
  }, []);

  React.useEffect(() => { fetchModuleStates(); }, [fetchModuleStates]);

  const toggleModule = async (companyId, moduleName, currentValue) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/company-modules/${companyId}`, 
        { [moduleName]: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModuleStates(prev => ({
        ...prev,
        [companyId]: { ...prev[companyId], [moduleName]: !currentValue }
      }));
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const fetchMpStatus = React.useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/system/payments/mercadopago`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMpEnabled(!!res.data.setting);
      setMpEnvAllowed(!!res.data.env_allowed);
    } catch (err) {
      console.error("Error fetching MercadoPago status:", err);
    }
  }, []);

  React.useEffect(() => { fetchMpStatus(); }, [fetchMpStatus]);

  const toggleMp = async () => {
    try {
      const token = localStorage.getItem("token");
      const newVal = !mpEnabled;
      await axios.post(`${API}/system/payments/mercadopago/toggle`, null, {
        params: { enabled: newVal },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMpEnabled(newVal);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loadingModules) return <div className="flex items-center justify-center h-32"><div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
        <span className="mr-3">🧩</span>
        Gestión de Módulos por Empresa
      </h2>
      <p className="text-sm text-gray-500 mb-6">Active o desactive módulos premium para cada empresa. Los procesos internos siguen funcionando independientemente del estado.</p>

      <div className="border border-gray-200 rounded-lg p-5 mb-6 bg-gradient-to-r from-sky-50 to-white" data-testid="mp-toggle-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800 flex items-center">
              <span className="mr-2">💳</span> Módulo de Pagos — MercadoPago
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {mpEnvAllowed
                ? "Habilita o deshabilita el cobro online por MercadoPago a nivel plataforma (solo Costillatech.com)."
                : "Deshabilitado por configuración del servidor (instancia marca blanca). No editable."}
            </p>
          </div>
          <div className="flex items-center">
            <span className={`mr-3 text-sm font-semibold ${mpEnabled && mpEnvAllowed ? "text-green-600" : "text-gray-400"}`}>
              {!mpEnvAllowed ? "FORZADO OFF" : (mpEnabled ? "ACTIVADO" : "DESACTIVADO")}
            </span>
            <button
              onClick={toggleMp}
              disabled={!mpEnvAllowed}
              data-testid="mp-toggle-btn"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                !mpEnvAllowed ? "bg-gray-200 cursor-not-allowed" : (mpEnabled ? "bg-green-500" : "bg-gray-300")
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${mpEnabled && mpEnvAllowed ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>
      </div>

      {isDemoOrDevEnv && (
      <div className="border-2 border-red-200 rounded-lg p-5 mb-6 bg-gradient-to-r from-red-50 to-white" data-testid="wipe-demo-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800 flex items-center">
              <span className="mr-2">🗑️</span> Vaciar Base de Datos Demo
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Borra <b>TODOS</b> los datos operativos (escaneos, vulnerabilidades, empresas, usuarios, targets, reportes, etc.). Preserva únicamente tu usuario administrador. Usar entre demos comerciales.
            </p>
            <p className="text-xs text-red-500 mt-1">Solo disponible en entornos DEV/DEMO. Bloqueado en producción.</p>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => setShowWipeConfirm(true)}
              disabled={wipingDemo}
              data-testid="wipe-demo-btn"
              className={`px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors ${wipingDemo ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
            >
              {wipingDemo ? "Vaciando..." : "Vaciar Base Demo"}
            </button>
          </div>
        </div>
      </div>
      )}

      {showWipeConfirm && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="wipe-demo-modal">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center">
            <span className="mr-2">⚠️</span> Confirmar vaciado de base demo
          </h3>
          <p className="text-sm text-gray-600 mt-3">
            Esta acción borrará <b>TODOS</b> los datos operativos de la base y no se puede deshacer.
            Solo se preservará tu usuario administrador.
          </p>
          <p className="text-sm text-gray-600 mt-2">¿Estás seguro?</p>
          <div className="flex justify-end space-x-3 mt-5">
            <button onClick={() => setShowWipeConfirm(false)}
              className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
              data-testid="wipe-demo-cancel">Cancelar</button>
            <button onClick={handleWipeDemo}
              className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
              data-testid="wipe-demo-confirm">Sí, vaciar la base</button>
          </div>
        </div>
      </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Empresa</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">Gestión de Riesgos</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">ISO 27001</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">SIEM</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">Plantillas ISO</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600 border-l border-gray-200">
                Ver Precios
                <span className="block text-[10px] font-normal text-gray-400 normal-case">panel de planes</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => {
              const modules = moduleStates[company.id] || {};
              return (
                <tr key={company.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{company.name}</p>
                    <p className="text-xs text-gray-400">{company.subscription_plan || "trial"}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleModule(company.id, "risk_management", modules.risk_management)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${modules.risk_management ? "bg-green-500" : "bg-gray-300"}`}
                      data-testid={`toggle-risk-${company.id}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${modules.risk_management ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleModule(company.id, "iso27001", modules.iso27001)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${modules.iso27001 ? "bg-green-500" : "bg-gray-300"}`}
                      data-testid={`toggle-iso-${company.id}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${modules.iso27001 ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleModule(company.id, "siem", modules.siem)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${modules.siem ? "bg-green-500" : "bg-gray-300"}`}
                      data-testid={`toggle-siem-${company.id}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${modules.siem ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleModule(company.id, "iso27001_templates", modules.iso27001_templates)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${modules.iso27001_templates ? "bg-green-500" : "bg-gray-300"}`}
                      data-testid={`toggle-iso-templates-${company.id}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${modules.iso27001_templates ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center border-l border-gray-200">
                    <button
                      onClick={() => toggleModule(company.id, "show_subscription_plans", modules.show_subscription_plans)}
                      title={modules.show_subscription_plans ? "El cliente VE el panel de Planes de Suscripcion" : "El cliente NO ve precios (se muestra Amenazas del Momento)"}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${modules.show_subscription_plans ? "bg-blue-600" : "bg-gray-300"}`}
                      data-testid={`toggle-show-plans-${company.id}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${modules.show_subscription_plans ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// Admin Panel Component for SuperAdmin
const AdminPanel = ({ onDataChange = () => {} }) => {
  const [adminTab, setAdminTab] = useState('overview');
  const [planRefreshTrigger, setPlanRefreshTrigger] = useState(0);
  const [stats, setStats] = useState({
    total_companies: 0,
    total_users: 0,
    total_targets: 0,
    total_scans: 0,
    total_vulnerabilities: 0,
    active_trials: 0,
    paying_customers: 0,
    monthly_revenue: 0,
    recent_companies: [],
    recent_scans: []
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [cuitEditando, setCuitEditando] = useState(null);
  const [cuitValor, setCuitValor] = useState("");
  const [cuitGuardando, setCuitGuardando] = useState(false);
  const [comsActivas, setComsActivas] = useState(null);
  const [comsGuardando, setComsGuardando] = useState(false);

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/admin/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching admin stats:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/admin/companies`, { headers });
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/system/maintenance`, { headers });
      setMaintenanceMode(response.data.maintenance_mode);
      setMaintenanceMessage(response.data.maintenance_message || "");
    } catch (error) {
      console.error("Error fetching maintenance status:", error);
    }
  };

  const fetchNotificationEmail = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/system/notification-email`, { headers });
      setNotificationEmail(response.data.notification_email || "");
    } catch (error) {
      console.error("Error fetching notification email:", error);
    }
  };

  const updateNotificationEmail = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/system/notification-email`, null, {
        params: { email: notificationEmail || null },
        headers
      });
      alert(notificationEmail ? "Email de notificación guardado" : "Email de notificación eliminado");
    } catch (error) {
      console.error("Error updating notification email:", error);
      alert("Error al guardar el email de notificación");
    }
  };

  const guardarCuit = async (companyId) => {
    const limpio = (cuitValor || "").replace(/[^0-9kK]/g, "");
    if (limpio.length < 7) {
      alert("El CUIT o DNI tiene que tener al menos 7 dígitos.");
      return;
    }
    setCuitGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.patch(`${API}/admin/companies/${companyId}/cuit`, { cuit: limpio }, { headers });
      setCuitEditando(null);
      setCuitValor("");
      fetchCompanies();
    } catch (error) {
      alert("No se pudo guardar el CUIT: " + (error.response?.data?.detail || error.message));
    } finally {
      setCuitGuardando(false);
    }
  };

  const fetchComsEstado = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/notifications/switch`, { headers });
      setComsActivas(!!res.data.activo);
    } catch (error) {
      console.error("Error leyendo el estado de las comunicaciones:", error);
    }
  };

  const toggleComunicaciones = async () => {
    const nuevo = !comsActivas;
    if (nuevo && !window.confirm(
      "Al activarlas, el sistema va a mandar mails de verdad a los administradores de todas las " +
      "empresas en el proximo ciclo (hasta 10 minutos). Las empresas de prueba con direcciones " +
      "inventadas van a generar rebotes.\n\n¿Activar el envio?")) return;
    setComsGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/notifications/switch`, null,
        { params: { activo: nuevo }, headers });
      setComsActivas(!!res.data.activo);
    } catch (error) {
      alert("Error al cambiar el envio: " + (error.response?.data?.detail || error.message));
    } finally {
      setComsGuardando(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const newMode = !maintenanceMode;
      await axios.post(`${API}/system/maintenance/toggle`, null, {
        params: { 
          maintenance_mode: newMode,
          message: maintenanceMessage || "Estamos realizando tareas de mantenimiento. Volveremos pronto."
        },
        headers
      });
      setMaintenanceMode(newMode);
      alert(newMode ? '✅ Modo mantenimiento ACTIVADO' : '✅ Modo mantenimiento DESACTIVADO');
    } catch (error) {
      alert("Error al cambiar modo mantenimiento: " + (error.response?.data?.detail || error.message));
    }
  };

  useEffect(() => {
    fetchAdminStats();
    fetchCompanies();
    fetchMaintenanceStatus();
    fetchNotificationEmail();
    fetchComsEstado();
  }, []);

  // Refresh plans when switching to plans tab
  useEffect(() => {
    if (adminTab === 'plans') {
      setPlanRefreshTrigger(prev => prev + 1);
    }
  }, [adminTab]);

  const updateCompanyPlan = async (companyId, newPlan) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/companies/${companyId}/plan`, null, {
        params: { new_plan: newPlan },
        headers
      });
      fetchCompanies();
      fetchAdminStats();
      alert(`Plan updated to ${newPlan}`);
    } catch (error) {
      alert("Error updating plan: " + (error.response?.data?.detail || error.message));
    }
  };

  const updateCompanyStatus = async (companyId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/companies/${companyId}/status`, null, {
        params: { new_status: newStatus },
        headers
      });
      fetchCompanies();
      alert(`Status updated to ${newStatus}`);
    } catch (error) {
      alert("Error updating status: " + (error.response?.data?.detail || error.message));
    }
  };

  if (loading && adminTab !== 'plans') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const adminTabs = [
    { id: 'overview', name: 'Resumen', icon: '📊' },
    { id: 'companies', name: 'Empresas', icon: '🏢' },
    { id: 'plans', name: 'Planes', icon: '💳' },
    { id: 'modules', name: 'Módulos', icon: '🧩' },
    { id: 'quotes', name: 'Consultas', icon: '📨' },
    { id: 'system', name: 'Sistema', icon: '⚙️' }
  ];

  return (
    <div className="space-y-6">
      {/* Admin Header */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h1 className="text-2xl font-bold text-red-800 flex items-center">
          <img src={COSTILLATECH_HEADER_LOGO} alt="Costillatech.com" className="h-8 w-auto mr-3" />
          Panel de Administración Costillatech.com
        </h1>
        <p className="text-red-600 mt-2">Vista global de la plataforma y gestión completa del sistema</p>
      </div>

      {/* Admin Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {adminTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setAdminTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                adminTab === tab.id
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {adminTab === 'overview' && (
        <div className="space-y-6">
          {/* Global Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
              <h3 className="text-lg font-semibold text-gray-700">Empresas Totales</h3>
              <p className="text-3xl font-bold text-blue-600">{stats.total_companies}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
              <h3 className="text-lg font-semibold text-gray-700">Usuarios Activos</h3>
              <p className="text-3xl font-bold text-green-600">{stats.total_users}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
              <h3 className="text-lg font-semibold text-gray-700">Trials Activos</h3>
              <p className="text-3xl font-bold text-yellow-600">{stats.active_trials}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
              <h3 className="text-lg font-semibold text-gray-700">Clientes Pagos</h3>
              <p className="text-3xl font-bold text-purple-600">{stats.paying_customers}</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
              <h3 className="text-lg font-semibold text-gray-700">Objetivos Totales</h3>
              <p className="text-3xl font-bold text-red-600">{stats.total_targets}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-indigo-500">
              <h3 className="text-lg font-semibold text-gray-700">Escaneos Totales</h3>
              <p className="text-3xl font-bold text-indigo-600">{stats.total_scans}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-pink-500">
              <h3 className="text-lg font-semibold text-gray-700">Vulnerabilidades</h3>
              <p className="text-3xl font-bold text-pink-600">{stats.total_vulnerabilities}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
              <h3 className="text-lg font-semibold text-gray-700">Ingreso Mensual</h3>
              <p className="text-3xl font-bold text-green-600">${stats.monthly_revenue.toFixed(0)}</p>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Empresas Recientes</h3>
              <div className="space-y-3">
                {stats.recent_companies.slice(0, 5).map((company) => (
                  <div key={company.id} className="flex items-center justify-between py-2 border-b">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{company.name}</p>
                      <p className="text-xs text-gray-500">{_UTC(company.created_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      company.subscription_plan === 'enterprise_exclusivo' ? 'bg-indigo-100 text-indigo-800' :
                      company.subscription_plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                      company.subscription_plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                      company.subscription_plan === 'basic' ? 'bg-green-100 text-green-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {company.subscription_plan === 'enterprise_exclusivo' ? 'ENTERPRISE EXCLUSIVO' : company.subscription_plan}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
              <div className="space-y-3">
                {stats.recent_scans.slice(0, 5).map((scan) => (
                  <div key={scan.id} className="flex items-center justify-between py-2 border-b">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Scan {scan.scan_type}</p>
                      <p className="text-xs text-gray-500">{_UTC(scan.created_at).toLocaleString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      scan.status === 'completed' ? 'bg-green-100 text-green-800' :
                      scan.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {scan.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {adminTab === 'companies' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Gestión de Empresas</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CUIT / DNI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuarios/Objetivos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trial Expira
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500">Creado: {_UTC(company.created_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'})}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-testid={`admin-cuit-${company.id}`}>
                      {cuitEditando === company.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            data-testid="admin-cuit-input"
                            autoFocus
                            value={cuitValor}
                            onChange={(e) => setCuitValor(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') guardarCuit(company.id); if (e.key === 'Escape') setCuitEditando(null); }}
                            placeholder="30712345678"
                            className="w-36 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button data-testid="admin-cuit-guardar" disabled={cuitGuardando}
                            onClick={() => guardarCuit(company.id)}
                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                            {cuitGuardando ? '...' : 'Guardar'}
                          </button>
                          <button onClick={() => setCuitEditando(null)}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid="admin-cuit-editar"
                          onClick={() => { setCuitEditando(company.id); setCuitValor(company.cuit || ""); }}
                          title="El CUIT es lo que une la empresa con su licencia"
                          className="text-left group">
                          {company.cuit ? (
                            <span className="text-sm text-gray-900 group-hover:text-blue-600 transition-colors">
                              {company.cuit} <span className="text-xs text-gray-400 group-hover:text-blue-500">editar</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">
                              Sin CUIT &middot; cargar
                            </span>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        company.subscription_plan === 'enterprise_exclusivo' ? 'bg-indigo-100 text-indigo-800' :
                        company.subscription_plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                        company.subscription_plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                        company.subscription_plan === 'basic' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {company.subscription_plan === 'enterprise_exclusivo' ? 'ENTERPRISE EXCLUSIVO' : company.subscription_plan.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        company.status === 'active' ? 'bg-green-100 text-green-800' :
                        company.status === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {company.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {company.total_users} usuarios / {company.total_targets} targets
                      <div className="text-xs text-gray-500">
                        {company.total_scans} escaneos / {company.total_vulnerabilities} vulns
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {company.trial_expires_at ? _UTC(company.trial_expires_at).toLocaleDateString('es-AR',{timeZone:'America/Argentina/Buenos_Aires'}) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <select
                          onChange={(e) => updateCompanyPlan(company.id, e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded"
                        >
                          <option value="">Cambiar Plan</option>
                          <option value="trial">Trial</option>
                          <option value="basic">Basic</option>
                          <option value="professional">Professional</option>
                          <option value="enterprise">Enterprise</option>
                          <option value="enterprise_exclusivo">Enterprise Exclusivo</option>
                        </select>
                        
                        <select
                          onChange={(e) => updateCompanyStatus(company.id, e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded"
                        >
                          <option value="">Estado</option>
                          <option value="active">Activo</option>
                          <option value="suspended">Suspendido</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adminTab === 'plans' && (
        <PlanManager 
          refreshTrigger={planRefreshTrigger} 
          onPlanChange={() => {
            setPlanRefreshTrigger(prev => prev + 1);
            // Also refresh admin stats if needed
            fetchAdminStats();
            // Notify parent to refresh dashboard
            onDataChange();
          }} 
        />
      )}

      {/* Sistema Tab - Modo Mantenimiento */}
      {adminTab === 'modules' && (
        <ModulesAdmin companies={companies} fetchCompanies={fetchCompanies} />
      )}

      {adminTab === 'quotes' && <QuoteRequestsAdmin />}

      {adminTab === 'system' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="mr-3">⚙️</span>
            Configuración del Sistema
          </h2>

          {/* Envio de comunicaciones */}
          <div className="border border-gray-200 rounded-lg p-6 mb-6" data-testid="admin-comunicaciones">
            <div className="flex items-center justify-between">
              <div className="pr-6">
                <h3 className="text-lg font-semibold text-gray-800">Envío de comunicaciones</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Controla los avisos por mail a los administradores de las empresas: hallazgos
                  críticos, resúmenes, recordatorios y agentes caídos. Apagado, el sistema sigue
                  funcionando igual pero no sale ningún mail. El cambio tarda hasta 10 minutos.
                </p>
              </div>
              <div className="flex items-center shrink-0">
                <span className={`mr-3 font-semibold ${comsActivas ? 'text-green-600' : 'text-gray-500'}`}
                  data-testid="admin-coms-estado">
                  {comsActivas === null ? '...' : comsActivas ? 'ACTIVADO' : 'DESACTIVADO'}
                </span>
                <button
                  data-testid="admin-coms-toggle"
                  onClick={toggleComunicaciones}
                  disabled={comsGuardando || comsActivas === null}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 ${
                    comsActivas
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {comsGuardando ? 'Guardando...' : comsActivas ? '✕ Desactivar' : '✉️ Activar'}
                </button>
              </div>
            </div>
          </div>

          {/* Modo Mantenimiento */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Modo Mantenimiento</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Activa el modo mantenimiento para bloquear el acceso de usuarios mientras realizas tareas de mantenimiento
                </p>
              </div>
              <div className="flex items-center">
                <span className={`mr-3 font-semibold ${maintenanceMode ? 'text-red-600' : 'text-green-600'}`}>
                  {maintenanceMode ? 'ACTIVADO' : 'DESACTIVADO'}
                </span>
                <button
                  onClick={toggleMaintenanceMode}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    maintenanceMode 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  {maintenanceMode ? '✓ Desactivar' : '🛠️ Activar'}
                </button>
              </div>
            </div>

            {/* Mensaje de mantenimiento */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensaje para usuarios:
              </label>
              <textarea
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Estamos realizando tareas de mantenimiento. Volveremos pronto."
              />
              <p className="text-xs text-gray-500 mt-1">
                Este mensaje se mostrará a los usuarios cuando intenten acceder al sistema durante el mantenimiento
              </p>
            </div>

            {/* Estado visual */}
            {maintenanceMode && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⚠️</span>
                  <div>
                    <p className="font-semibold text-red-800">Sistema en modo mantenimiento</p>
                    <p className="text-sm text-red-600">Los usuarios no pueden acceder al sistema excepto SuperAdmin</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Email de Notificación */}
          <div className="border border-gray-200 rounded-lg p-6 mt-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-800">📧 Email de Notificación</h3>
              <p className="text-sm text-gray-600 mt-1">
                Recibe una notificación por email cada vez que un nuevo usuario se registre en la plataforma
              </p>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="email"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="tu-email@ejemplo.com"
              />
              <button
                onClick={updateNotificationEmail}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                💾 Guardar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Datos incluidos: Usuario, Empresa, Email, Objetivos declarados. Deja vacío para desactivar las notificaciones.
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

// Authorized Targets Management Page
const AuthorizedTargetsPage = () => {
  const { token } = useAuth();
  const [authorizations, setAuthorizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTarget, setNewTarget] = useState({
    target_type: 'domain',
    target_value: '',
    authorization_statement: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAuthorizations();
  }, []);

  const fetchAuthorizations = async () => {
    try {
      const response = await axios.get(`${API}/scan-authorizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthorizations(response.data.authorizations);
    } catch (error) {
      console.error('Error fetching authorizations:', error);
      setError('Error al cargar dominios autorizados');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAuthorization = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newTarget.target_value.trim()) {
      setError('Debes especificar un dominio o IP');
      return;
    }

    if (!newTarget.authorization_statement.trim() || newTarget.authorization_statement.length < 50) {
      setError('La declaración debe tener al menos 50 caracteres');
      return;
    }
    if (!termsAccepted) {
      setError('Debes confirmar que aceptas los términos y políticas');
      return;
    }

    try {
      await axios.post(`${API}/scan-authorizations`, newTarget, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('✓ Dominio autorizado correctamente');
      setNewTarget({
        target_type: 'domain',
        target_value: '',
        authorization_statement: ''
      });
      setTermsAccepted(false);
      setShowAddForm(false);
      fetchAuthorizations();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al autorizar dominio');
    }
  };

  const handleRevokeAuthorization = async (authId) => {
    if (!window.confirm('¿Estás seguro de revocar esta autorización? No podrás escanear este dominio hasta que lo autorices nuevamente.')) {
      return;
    }

    try {
      await axios.delete(`${API}/scan-authorizations/${authId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('✓ Autorización revocada correctamente');
      fetchAuthorizations();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al revocar autorización');
    }
  };

  const detectTargetType = (value) => {
    if (value.startsWith('*.')) return 'subdomain_wildcard';
    if (value.includes('/')) return 'ip_range';
    try {
      // Check if it's an IP
      const parts = value.split('.');
      if (parts.length === 4 && parts.every(p => !isNaN(p) && parseInt(p) >= 0 && parseInt(p) <= 255)) {
        return 'ip';
      }
    } catch (e) {}
    return 'domain';
  };

  const getTargetTypeLabel = (type) => {
    const labels = {
      'domain': '🌐 Dominio',
      'subdomain_wildcard': '🔀 Wildcard',
      'ip': '🔢 IP',
      'ip_range': '📡 Rango IP'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">⚖️ Dominios Autorizados</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gestiona los dominios e IPs que tienes autorización legal para escanear
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {showAddForm ? 'Cancelar' : '+ Autorizar Nuevo Dominio'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Autorizar Nuevo Dominio/IP</h3>
          <form onSubmit={handleAddAuthorization} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dominio o IP a autorizar
              </label>
              <input
                type="text"
                value={newTarget.target_value}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewTarget({
                    ...newTarget,
                    target_value: value,
                    target_type: detectTargetType(value)
                  });
                }}
                placeholder="ejemplo.com, *.ejemplo.com, 203.0.113.45, 203.0.113.0/24"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                Tipo detectado: {getTargetTypeLabel(newTarget.target_type)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Declaración de Autorización (mínimo 50 caracteres)
              </label>
              <textarea
                value={newTarget.authorization_statement}
                onChange={(e) => setNewTarget({...newTarget, authorization_statement: e.target.value})}
                placeholder="Yo, [Nombre], en mi calidad de [Cargo] de [Empresa], declaro que tengo autorización expresa para realizar escaneos de seguridad en el dominio/IP especificado..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                {newTarget.authorization_statement.length} / 50 caracteres mínimos
              </div>
            </div>
           <div className="flex items-start p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <input
                type="checkbox"
                id="termsAcceptedNew"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600"
              />
              <label htmlFor="termsAcceptedNew" className="ml-3 text-sm text-gray-700">
                Confirmo que he leído y sigo aceptando los{' '}
                <a href="/legal/terms-v1.0.html" target="_blank" className="text-blue-600 hover:underline">
                  Términos y Condiciones
                </a>,{' '}
                <a href="/legal/privacy-v1.0.html" target="_blank" className="text-blue-600 hover:underline">
                  Política de Privacidad
                </a>,{' '}
                <a href="/legal/msa-v1.0.html" target="_blank" className="text-blue-600 hover:underline">
                  Contrato de Servicios
                </a>{' '}
                y la{' '}
                <a href="/legal/acceptable-use-v1.0.html" target="_blank" className="text-blue-600 hover:underline">
                  Política de Uso Aceptable
                </a>.
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Autorizar Dominio
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Authorizations List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            Dominios Autorizados ({authorizations.length})
          </h3>
        </div>

        {authorizations.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 text-5xl mb-4">🔒</div>
            <p className="text-gray-600 mb-2">No tienes dominios autorizados</p>
            <p className="text-sm text-gray-500">
              Debes autorizar al menos un dominio antes de poder realizar escaneos
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {authorizations.map((auth) => (
              <div key={auth.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{getTargetTypeLabel(auth.target_type).split(' ')[0]}</span>
                      <span className="text-lg font-medium text-gray-900">{auth.target_value}</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                        Activo
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      <p><strong>Autorizado:</strong> {_UTC(auth.authorized_at).toLocaleString('es-AR')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeAuthorization(auth.id)}
                    className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                  >
                    Revocar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legal Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Información Legal</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Solo puedes escanear dominios/IPs que hayas autorizado explícitamente</li>
          <li>• Debes tener autorización legal del propietario de cada dominio/IP</li>
          <li>• Todas las autorizaciones quedan registradas con fines de auditoría</li>
          <li>• El escaneo no autorizado puede tener consecuencias legales</li>
        </ul>
      </div>
    </div>
  );
};


// Main App Component with Welcome Page
const MainApp = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAdvancedDropdown, setShowAdvancedDropdown] = useState(false);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0);
  const [publicPlans, setPublicPlans] = useState([]);
  const [companyModules, setCompanyModules] = useState({ risk_management: false, iso27001: false, siem: false, iso27001_templates: false, show_subscription_plans: false });
  const { user, handlePlanPurchase } = useAuth();

  // Si esta consulta falla, los 3 modulos premium quedan como no habilitados y el menu
  // se ve incompleto, asi que se reintenta en vez de fallar en silencio.
  const fetchCompanyModules = async (attempt = 0) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/company/modules`, { headers: { Authorization: `Bearer ${token}` } });
      setCompanyModules(res.data);
    } catch (err) {
      console.error("Error fetching modules:", err);
      if (attempt < 3) setTimeout(() => fetchCompanyModules(attempt + 1), 1500 * (attempt + 1));
    }
  };

  useEffect(() => { fetchCompanyModules(); }, []);

  const fetchPublicPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      console.log('🔄 Fetched public plans:', response.data); // Debug log
      
      // Convert the plans object to an array format expected by the frontend
      if (response.data.plans) {
        const plansArray = Object.entries(response.data.plans).map(([plan_code, planData]) => ({
          id: planData.id,
          plan_code: plan_code,
          name: planData.name,
          description: planData.description,
          price_usd: planData.pricing.usd,
          price_ars: planData.pricing.ars,
          price_eur: planData.pricing.eur,
          default_currency: planData.pricing.default_currency,
          price_monthly_usd: planData.pricing.monthly_usd,
          discount_annual_pct: planData.pricing.discount_annual_pct || 0,
          billing_type: planData.billing_type || "recurring",
          is_one_shot: !!planData.is_one_shot,
          targets_limit: planData.limits.targets,
          scans_per_month: planData.limits.scans_per_month,
          storage_gb: planData.limits.storage_gb,
          api_calls_limit: planData.limits.api_calls,
          trial_duration_days: 0,
          features: planData.features
        }));
        setPublicPlans(plansArray);
      } else {
        setPublicPlans([]);
      }
    } catch (error) {
      console.error("Error fetching public plans:", error);
      setPublicPlans([]);
    }
  };

  const refreshDashboard = () => {
    setRefreshKey(prev => prev + 1);
    setDashboardRefreshTrigger(prev => prev + 1);
    fetchPublicPlans(); // Also refresh public plans
  };

  // Fetch public plans on component mount
  useEffect(() => {
    fetchPublicPlans();
  }, []);

  const getPlanDisplayColor = (plan_code) => {
    switch (plan_code?.toLowerCase()) {
      case 'trial': return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', subtext: 'text-yellow-700' };
      case 'basic': return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-900', subtext: 'text-gray-600' };
      case 'professional': return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', subtext: 'text-blue-700' };
      case 'enterprise': return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', subtext: 'text-purple-700' };
      case 'enterprise_exclusivo': return { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800', subtext: 'text-indigo-700' };
      default: return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-900', subtext: 'text-gray-600' };
    }
  };

  const formatPlanPrice = (plan) => {
    if (plan.plan_code?.toLowerCase() === 'trial') {
      return 'Prueba Gratuita';
    }
    
    const currency = plan.default_currency || 'usd';
    let price = 0;
    let symbol = '$';
    
    switch (currency) {
      case 'usd':
        price = plan.price_usd || 0;
        symbol = 'U\$S ';
        break;
      case 'ars':
        price = plan.price_ars || 0;
        symbol = '$';
        break;
      case 'eur':
        price = plan.price_eur || 0;
        symbol = '€';
        break;
    }
    
    return `${symbol}${price}/año`;
  };

  const formatPlanLimits = (plan) => {
    if (plan.plan_code?.toLowerCase() === 'trial') {
      return `${plan.targets_limit || 1} target${plan.targets_limit > 1 ? 's' : ''}, ${plan.scans_per_month || 3} escaneos, ${plan.trial_duration_days || 1} día${plan.trial_duration_days > 1 ? 's' : ''}`;
    }
    
    const targets = plan.targets_limit === -1 ? 'Ilimitado' : `${plan.targets_limit} targets`;
    const scans = plan.scans_per_month === -1 ? 'Ilimitado' : `${plan.scans_per_month} escaneos/mes`;
    
    if (plan.plan_code?.toLowerCase() === 'enterprise' || plan.plan_code?.toLowerCase() === 'enterprise_exclusivo') {
      return `${targets}, ${scans}, soporte 24/7`;
    }
    
    return `${targets}, ${scans}`;
  };

  const isSuperAdmin = user?.role === 'superadmin';
  const isModuleEnabled = (moduleId) => {
    if (isSuperAdmin) return true;
    if (moduleId === 'risk_management') return companyModules.risk_management;
    if (moduleId === 'iso27001') return companyModules.iso27001;
    if (moduleId === 'siem') return companyModules.siem;
    if (moduleId === 'iso27001_templates') return companyModules.iso27001_templates;
    return true;
  };

  const tabs = user?.role === 'superadmin' ? [
    { id: 'admin', name: 'Admin Panel', icon: '⚙️' },
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'targets', name: 'Objetivos', icon: '🎯' },
    { id: 'scans', name: 'Escaneos', icon: '🔍' },
    { id: 'vulnerabilities', name: 'Vulnerabilidades', icon: '⚠️' },
    { id: 'scan_details', name: 'Detalles', icon: '🔬' },
    { id: 'risk_dashboard', name: 'Risk Analysis', icon: '🎯' },
    { id: 'risk_management', name: 'Gestión de Riesgos', icon: '🛡️' },
    { id: 'iso27001', name: 'ISO 27001', icon: '📋' },
    { id: 'siem', name: 'SIEM', icon: '🔔', premium: true }
  ] : [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'targets', name: 'Objetivos', icon: '🎯' },
    { id: 'scans', name: 'Escaneos', icon: '🔍' },
    { id: 'vulnerabilities', name: 'Vulnerabilidades', icon: '⚠️' },
    { id: 'scan_details', name: 'Detalles', icon: '🔬' },
    { id: 'risk_dashboard', name: 'Risk Analysis', icon: '🎯' },
    { id: 'risk_management', name: 'Gestión de Riesgos', icon: '🛡️', premium: true },
    { id: 'iso27001', name: 'ISO 27001', icon: '📋', premium: true },
    { id: 'siem', name: 'SIEM', icon: '🔔', premium: true }
  ];

  // Módulos de Escaneo Avanzado
  const advancedScanModules = [
    { id: 'osint', name: 'OSINT Scanner', icon: '🔍', description: 'Inteligencia de Código Abierto', disabled: false },
    { id: 'advanced-web', name: 'Advanced Web Testing', icon: '🌐', description: 'Pruebas Avanzadas de Aplicaciones Web', disabled: false },
    { id: 'network-enum', name: 'Network Enumeration', icon: '🌐', description: 'Enumeración de Red', disabled: false },
    { id: 'security-posture', name: 'Postura de Seguridad', icon: '🛡️', description: 'Detección AV, Firewall, VPN', disabled: false },
    { id: 'api-security', name: 'Scanner de APIs', icon: '🔌', description: 'OWASP API Top 10', disabled: false },
    { id: 'web-hygiene', name: 'Higiene Web (SSRF/CSRF)', icon: '🧼', description: 'Controles CSRF y SSRF', disabled: false },
    { id: 'ai-bot-security', name: 'Scanner de Bots IA', icon: '🤖', description: 'Seguridad de Chatbots', disabled: false },
    { id: 'demo-chatbot', name: 'Demo Chatbot', icon: '💬', description: 'Bot de Prueba Vulnerable', disabled: false },
    { id: 'dark-web', name: 'Scanner Dark Web', icon: '🌑', description: 'Credenciales Expuestas', disabled: false },
    { id: 'brand-impersonation', name: 'Brand Impersonation', icon: '🎭', description: 'Suplantación de Marca', disabled: false },
    { id: 'ai-attack-readiness', name: 'Defensa contra IAs Maliciosas', icon: '🤖', description: 'Evaluación de amenazas AI', disabled: false },
    { id: 'microsegmentation', name: 'Microsegmentación de Red (Ransomware)', icon: '🛡️', description: 'Detección de segmentación', disabled: false },
    { id: 'usb-policy', name: 'Control de Medios Extraíbles', icon: '🔌', description: 'Políticas USB/Pendrive', disabled: false },
    { id: 'internal-agent', name: 'Agente Interno', icon: '🖥️', description: 'Escaneo de sistemas internos y legacy', disabled: false },
    { id: 'executive-report', name: 'Resumen Ejecutivo', icon: '📊', description: 'Reportes y Análisis', disabled: false }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
     <Header setActiveTab={setActiveTab} />

      {/* Navigation Tabs */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const disabled = tab.premium && !isModuleEnabled(tab.id);
              return (
              <button
                key={tab.id}
                onClick={() => disabled ? alert('Módulo premium no activado. Contacte al administrador.') : setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  disabled ? 'border-transparent text-gray-400 cursor-help' :
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`nav-tab-${tab.id}`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
                {disabled && <span title="Modulo premium no activado" className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full align-middle">&#128274; PRO</span>}
              </button>
              );
            })}
            
            {/* Dropdown Escaneos Avanzados */}
            <div className="relative">
              <button
                onClick={() => setShowAdvancedModal(true)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center ${
                  ['osint', 'advanced-web', 'network-enum', 'security-posture', 'api-security', 'ai-bot-security', 'demo-chatbot', 'dark-web', 'brand-impersonation', 'ai-attack-readiness', 'microsegmentation', 'usb-policy', 'internal-agent', 'executive-report'].includes(activeTab)
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">🔬</span>
                Escaneos Avanzados
                <span className="ml-1">{'⚡'}</span>
              </button>
              
              {showAdvancedDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  {advancedScanModules.map((module) => (
                    <button
                      key={module.id}
                      onClick={() => {
                        setActiveTab(module.id);
                        setShowAdvancedDropdown(false);
                      }}
                      disabled={module.disabled}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        module.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      } ${activeTab === module.id ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      <div className="flex items-center">
                        <span className="text-xl mr-3">{module.icon}</span>
                        <div>
                          <div className="font-medium">{module.name}</div>
                          <div className="text-xs text-gray-500">{module.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Modal de Escaneos Avanzados */}
      {showAdvancedModal && (
        <AdvancedScannersGrid
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onClose={() => setShowAdvancedModal(false)}
        />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {activeTab === 'admin' && <AdminPanel onDataChange={refreshDashboard} />}
        {activeTab === 'dashboard' && (
          <div>
            <Dashboard key={refreshKey} refreshTrigger={dashboardRefreshTrigger} showPlans={!!companyModules.show_subscription_plans} />
            <SurfaceChangesPanel />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-6 w-6 mr-2" />
                  Estándares de Seguridad
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">ISO</div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">ISO 27001</p>
                      <p className="text-sm text-gray-600">Gestión de Seguridad de la Información</p>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                    <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white text-sm font-bold">OW</div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">OWASP Top 10</p>
                      <p className="text-sm text-gray-600">Riesgos de Seguridad en Aplicaciones Web</p>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-green-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">NI</div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">NIST Framework</p>
                      <p className="text-sm text-gray-600">Marco de Ciberseguridad</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-5 w-5 mr-2" />
                    Transparencia del Código
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">Esta plataforma está construida con principios éticos de seguridad:</p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>✅ Algoritmos de escaneo abiertos - sin backdoors ocultos</li>
                    <li>✅ Detección transparente de vulnerabilidades</li>
                    <li>✅ Sin malware o troyanos - solo hacking ético</li>
                    <li>✅ Cumplimiento con estándares de seguridad</li>
                    <li>✅ Privacidad de datos y aislamiento por empresa</li>
                  </ul>
                </div>
              </div>
              
              {companyModules.show_subscription_plans
                ? <PlanesSuscripcion publicPlans={publicPlans} onPurchase={handlePlanPurchase} />
                : <KevThreatsPanel />}
            </div>
          </div>
        )}
        
        {activeTab === 'targets' && (isAdmin(user)
          ? <TargetManager onTargetAdded={refreshDashboard} />
          : <RoleBlocked required="Administrador" user={user}
              action="dar de alta objetivos y firmar autorizaciones de escaneo" />)}
        {activeTab === 'scans' && (canOperate(user)
          ? <ScanManager refreshDashboard={refreshDashboard} />
          : <RoleBlocked required="Auditor" user={user} action="ejecutar escaneos" />)}
        {activeTab === 'vulnerabilities' && <VulnerabilityView />}
        {activeTab === 'scan_details' && <ScanDetailsView />}
        {activeTab === 'risk_management' && <RiskManagement />}
        {activeTab === 'iso27001' && <ISO27001Management companyModules={companyModules} />}
        {activeTab === "siem" && <SIEMDashboard />}
        {activeTab === 'risk_dashboard' && <RiskDashboard />}
        {activeTab === 'osint' && <OSINTScanner />}
        {activeTab === 'advanced-web' && <AdvancedWebScanner />}
        {activeTab === 'network-enum' && <NetworkEnumScanner />}
        {activeTab === 'security-posture' && <SecurityPostureScanner />}
        {activeTab === 'api-security' && <APISecurityScanner />}
        {activeTab === 'web-hygiene' && <WebHygieneScanner />}
        {activeTab === 'ai-bot-security' && <AIBotSecurityScanner />}
        {activeTab === 'dark-web' && <DarkWebScanner />}
        {activeTab === 'brand-impersonation' && <BrandImpersonationScanner />}
        {activeTab === 'demo-chatbot' && <DemoChatbot />}
        {activeTab === 'ai-attack-readiness' && <AIAttackReadinessScanner />}
        {activeTab === 'microsegmentation' && <MicrosegmentationScanner />}
        {activeTab === 'usb-policy' && <USBPolicyScanner />}
        {activeTab === 'internal-agent' && <InternalAgentScanner />}
        {activeTab === 'executive-report' && <ExecutiveReport setActiveTab={setActiveTab} VerModule={VerModule} />}
        {activeTab === 'authorized-targets' && <AuthorizedTargetsPage />}
        {activeTab === 'users' && <UsersManagement />}
      </main>
    </div>
  );
};


// ===== Planes de Suscripcion (con toggle Mensual/Anual + One-Shot) =====
const PlanesSuscripcion = ({ publicPlans, onPurchase }) => {
  const [billingCycle, setBillingCycle] = React.useState("monthly");

  if (!publicPlans || publicPlans.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Planes de Suscripción</h2>
        <div className="text-center py-8">
          <div className="text-4xl mb-2">💳</div>
          <p className="text-gray-500">Cargando planes de suscripción...</p>
        </div>
      </div>
    );
  }

  const order = { basic: 1, professional: 2, enterprise: 3, enterprise_exclusivo: 4 };
  const recurring = publicPlans
    .filter(p => !p.is_one_shot && p.plan_code !== "one_shot")
    .sort((a, b) => (order[a.plan_code?.toLowerCase()] || 99) - (order[b.plan_code?.toLowerCase()] || 99));
  const oneShots = publicPlans.filter(p => p.is_one_shot || p.plan_code === "one_shot");

  const priceFor = (plan) => {
    if (plan.is_one_shot) return { amount: plan.price_monthly_usd || plan.price_usd || 0, suffix: "único" };
    const monthly = Number(plan.price_monthly_usd || 0);
    const disc = Number(plan.discount_annual_pct || 0);
    if (billingCycle === "monthly") return { amount: monthly, suffix: "/mes" };
    const annualDiscounted = Math.round(monthly * 12 * (1 - disc / 100));
    return { amount: annualDiscounted, suffix: "/año", monthly };
  };

  const fmt = (n) => "u$s " + Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Planes de Suscripción</h2>
        <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="billing-toggle">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${billingCycle === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
            data-testid="billing-toggle-monthly"
          >Mensual</button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all flex items-center gap-1.5 ${billingCycle === "annual" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
            data-testid="billing-toggle-annual"
          >
            Anual
            <span className="text-xs font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">-15%</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {recurring.map(plan => {
          const price = priceFor(plan);
          const isEnterprise = plan.plan_code === "enterprise" || plan.plan_code === "enterprise_exclusivo";
          const isExclusivo = plan.plan_code === "enterprise_exclusivo";
          return (
            <div key={plan.id} className={`relative border rounded-xl p-4 transition-all hover:shadow-md ${isExclusivo ? "border-purple-300 bg-gradient-to-br from-purple-50 to-white" : isEnterprise ? "border-purple-200 bg-purple-50/40" : "border-gray-200 bg-white"}`}>
              {isExclusivo && (
                <span className="absolute -top-2.5 right-3 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-purple-600 to-pink-500 text-white px-2 py-0.5 rounded-full shadow">Premium</span>
              )}
              <h4 className="font-semibold text-gray-900">{plan.name}</h4>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-gray-900">{fmt(price.amount)}</span>
                <span className="text-xs text-gray-500">{price.suffix}</span>
              </div>
              {billingCycle === "annual" && price.monthly && (
                <p className="text-[11px] text-gray-500 mt-0.5">Equivale a <b>{fmt(Math.round(price.amount / 12))}/mes</b> (facturación anual)</p>
              )}
              <p className="text-xs text-gray-600 mt-2">
                {plan.targets_limit === -1 || plan.targets_limit >= 999 ? "Objetivos ilimitados" : `${plan.targets_limit} targets`}
                {" · "}
                {plan.scans_per_month === -1 || plan.scans_per_month >= 999 ? "Escaneos ilimitados" : `${plan.scans_per_month} escaneos/mes`}
              </p>
              {plan.description && (
                <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{plan.description}</p>
              )}
              <button
                onClick={() => onPurchase(plan)}
                className="mt-3 w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium text-sm shadow-sm"
                data-testid={`plan-purchase-${plan.plan_code}`}
              >💳 Contratar</button>
            </div>
          );
        })}
      </div>

      {oneShots.length > 0 && (
        <div className="border-t pt-5">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-600">Producto puntual</h3>
            <span className="text-[10px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full">Pago único</span>
          </div>
          {oneShots.map(plan => (
            <div key={plan.id} className="relative border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4">
              <h4 className="font-semibold text-gray-900">{plan.name}</h4>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-3xl font-bold text-emerald-700">{fmt(plan.price_monthly_usd || plan.price_usd)}</span>
                <span className="text-xs text-gray-500">pago único</span>
              </div>
              <p className="text-xs text-gray-700 mt-2">
                <b>{plan.targets_limit} targets · {plan.scans_per_month} escaneos totales</b> (no mensuales) + Informe Gerencial
              </p>
              {plan.description && (<p className="text-[11px] text-gray-600 mt-1">{plan.description}</p>)}
              <button
                onClick={() => onPurchase(plan)}
                className="mt-3 w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-2 rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all font-medium text-sm shadow-sm"
                data-testid="plan-purchase-one_shot"
              >Comprar One-Shot</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Payment Result Pages
const PaymentSuccess = () => {
  const [countdown, setCountdown] = useState(10);
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  
  // Get preference_id from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const preferenceId = urlParams.get('preference_id');
  
  const activateSubscription = async () => {
    if (!preferenceId) {
      alert('No se encontró ID de preferencia para activar la suscripción');
      return;
    }
    
    setActivating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/payments/simulate-success?preference_id=${preferenceId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        setActivated(true);
        alert('¡Suscripción activada exitosamente!');
      } else {
        alert('Error activando la suscripción');
      }
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.response?.data?.detail || 'Error activando suscripción'}`);
    } finally {
      setActivating(false);
    }
  };
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          window.location.href = '/';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <div className="text-6xl mb-4">✅</div>
        <h1 className="text-2xl font-bold text-green-600 mb-4">¡Pago Completado!</h1>
        
        {!activated ? (
          <>
            <p className="text-gray-700 mb-6">
              El pago se procesó correctamente. Para activar tu suscripción en el entorno de pruebas, 
              haz click en el botón de abajo:
            </p>
            <button 
              onClick={activateSubscription}
              disabled={activating}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 mb-4 w-full"
            >
              {activating ? '🔄 Activando...' : '🚀 Activar Suscripción'}
            </button>
          </>
        ) : (
          <p className="text-gray-700 mb-6">
            ¡Tu suscripción ha sido activada correctamente! 
            Ahora puedes disfrutar de todas las funcionalidades de tu plan.
          </p>
        )}
        
        <p className="text-sm text-gray-500 mb-4">
          Serás redirigido automáticamente en {countdown} segundos
        </p>
        <button 
          onClick={() => window.location.href = '/'}
          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
        >
          Ir al Dashboard
        </button>
      </div>
    </div>
  );
};

const PaymentFailure = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <div className="text-6xl mb-4">❌</div>
        <h1 className="text-2xl font-bold text-red-600 mb-4">Pago Fallido</h1>
        <p className="text-gray-700 mb-6">
          Hubo un problema procesando tu pago. 
          Por favor, intenta nuevamente o contacta con soporte.
        </p>
        <div className="space-y-3">
          <button 
            onClick={() => window.location.href = '/'}
            className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Volver al Dashboard
          </button>
          <button 
            onClick={() => window.history.back()}
            className="w-full bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
          >
            Intentar de Nuevo
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentPending = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <div className="text-6xl mb-4">⏳</div>
        <h1 className="text-2xl font-bold text-yellow-600 mb-4">Pago Pendiente</h1>
        <p className="text-gray-700 mb-6">
          Tu pago está siendo procesado. Te notificaremos cuando se complete la transacción.
        </p>
        <button 
          onClick={() => window.location.href = '/'}
          className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700"
        >
          Ir al Dashboard
        </button>
      </div>
    </div>
  );
};

// Maintenance Page Component
const MaintenancePage = () => {
  // Get maintenance message from localStorage
  const maintenanceMessage = localStorage.getItem('maintenance_message') || 'Estamos realizando tareas de mantenimiento. Volveremos pronto.';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <div className="text-6xl mb-4">🔧</div>
        <h1 className="text-2xl font-bold text-purple-600 mb-4">Mantenimiento en Progreso</h1>
        <p className="text-gray-700 mb-6" style={{ whiteSpace: 'pre-line' }}>
          {maintenanceMessage}
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mt-4">
          <p className="text-sm text-gray-600">
            Para consultas urgentes, contacta a:<br />
            <strong className="text-purple-600">soporte@costillatech.com</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

// Simple Router for Payment Pages
const SimpleRouter = () => {
  const path = window.location.pathname;
  
  switch (path) {
    case '/payment/success':
      return <PaymentSuccess />;
    case '/payment/failure':
      return <PaymentFailure />;
    case '/payment/pending':
      return <PaymentPending />;
    case '/forgot-password':
      return <ForgotPasswordPage />;
    case '/reset-password':
      return <ResetPasswordPage />;
    case '/maintenance':
      return <MaintenancePage />;
    default:
      return <SeguriaApp />;
  }
};

// Main App Component
const SeguriaApp = () => {
  const [showWelcome, setShowWelcome] = useState(false); // Costillatech: sin WelcomePage
  const [isLogin, setIsLogin] = useState(true);

  const handleGetStarted = () => {
    setShowWelcome(false);
    localStorage.setItem('seguria_welcome_seen', 'true');
  };

  if (showWelcome) {
    return <WelcomePage onGetStarted={handleGetStarted} />;
  }

  return (
    <AuthProvider>
      <AuthContent isLogin={isLogin} setIsLogin={setIsLogin} />
    </AuthProvider>
  );
};

// Root App Component with Simple Routing
function App() {
  return <SimpleRouter />;
}

const AuthContent = ({ isLogin, setIsLogin }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src={COSTILLATECH_LOGO} alt="Costillatech.com" className="h-16 w-16 mx-auto mb-4 animate-pulse" />
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return isLogin ? (
      <LoginForm onSwitchToRegister={() => setIsLogin(false)} />
    ) : (
      <RegisterForm onSwitchToLogin={() => setIsLogin(true)} />
    );
  }

  return (
    <>
      <MainApp />
      <CookieBanner />
    </>
  );
};

export default App;
